import { luminance, mixColor, withAlpha, type Color } from "../../common/color.js";
import type { Point, Rect } from "../../common/geometry.js";
import { Control, type ControlOptions } from "../core/Control.js";
import type { KeyEventArgs, MouseEventArgs, WheelEventArgs } from "../core/events.js";
import { strokeTriangles, type Triangle } from "../art/markup/shapes.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import { linkOf, type ChartLink } from "./ChartLink.js";
import { squarify, volumeProfile, waterfall, type WaterfallStep } from "./chartFinance.js";
import {
  bandTriangles,
  categoryX,
  decimalsFor,
  formatMoment,
  isOhlc,
  labelStride,
  linearScale,
  logScale,
  nearestCategory,
  panView,
  periodTicks,
  pieSlices,
  scaleFraction,
  scaleValue,
  sliceAt,
  stackValues,
  stepPoints,
  timeScale,
  valueExtent,
  viewScale,
  zoomView,
  type ChartScale,
  type Ohlc,
  type PieSlice,
  type ScaleKind,
  type Stack,
} from "./chartScale.js";

/**
 * How one series is drawn on a cartesian chart: a `line`, a filled `area`, a `bar`, a `step` line
 * that holds each value level until halfway to the next, or bare `points` with nothing joining them
 * - or, from a series' `ohlc` prices, a `candlestick` or an `ohlc` bar per period - or a `band`,
 * filled between its `lower` values and its `values`, as Bollinger Bands are.
 *
 * And the rest of a trader's kit: `range` bars floating from `lower` to the value; a `baseline`
 * area, green above `baseValue` and red below; a `waterfall` of changes floating from a running
 * total; and the time-free price charts - `renko` bricks and `pointfigure` columns of Xs and Os
 * from `ohlc` periods (see `renko` and `pointFigure`), and a `kagi` line through turning points
 * (see `kagi`), thick where it climbs past its last shoulder and thin where it falls through its
 * last waist.
 */
export type ChartSeriesKind =
  | "line"
  | "area"
  | "bar"
  | "step"
  | "points"
  | "candlestick"
  | "ohlc"
  | "band"
  | "range"
  | "baseline"
  | "waterfall"
  | "renko"
  | "kagi"
  | "pointfigure";

/** One value of a `band` series: the two lines it fills between. */
export interface ChartRange {
  lower: number;
  upper: number;
}

const isRange = (v: unknown): v is ChartRange =>
  v !== null &&
  typeof v === "object" &&
  Number.isFinite((v as ChartRange).lower) &&
  Number.isFinite((v as ChartRange).upper);

/** True for the kinds drawn from a series' open, high, low and close rather than its values. */
export const isFinancialKind = (kind: ChartSeriesKind): boolean =>
  kind === "candlestick" || kind === "ohlc" || kind === "renko" || kind === "pointfigure";

/** True for the kinds that take a slot of their own per category, as a bar does. */
const takesSlot = (kind: ChartSeriesKind): boolean => kind === "bar" || kind === "range" || kind === "waterfall" || isFinancialKind(kind);

/**
 * The shape of the whole chart. `cartesian` plots series against a value axis; `pie` and `donut`
 * cut each series into slices, one per category, with a second series as a second ring; `treemap`
 * tiles the plot with one rectangle per category sized by the first series - and, when there is a
 * second, colored by it: green for a rise and red for a fall, deeper the further it went, the
 * portfolio heat map.
 */
export type ChartType = "cartesian" | "pie" | "donut" | "treemap";

/** `vertical` grows values upward from categories along the bottom; `horizontal` lies it on its side. */
export type ChartOrientation = "vertical" | "horizontal";

/**
 * What the category axis is. `category` spaces the values evenly under their labels; the others
 * place each at its x - `linear`, `log`, or `time` in epoch milliseconds or `Date`s.
 */
export type ChartXScale = "category" | ScaleKind;

/** Which value axis a series is measured against: the main one, or the one on the opposite side. */
export type ChartValueAxis = "primary" | "secondary";

/** The visible part of the category axis, as fractions of the whole: 0..1 when not zoomed. */
export interface ChartView {
  from: number;
  to: number;
}

export interface ChartSeries {
  name: string;
  /**
   * One value per category or x; null is a gap. A series with `ohlc` prices may leave it out, and
   * gets each period's close.
   */
  values?: readonly (number | null)[];
  /** Open, high, low and close for each period, for a `candlestick` or `ohlc` series; null is a gap. */
  ohlc?: readonly (Ohlc | null)[];
  /** The bottom edge of a `band` or a `range` bar, whose `values` are its top. */
  lower?: readonly (number | null)[];
  /** How a line or area joins its points: straight, or in steps, as a market depth chart is drawn. */
  curve?: "linear" | "step";
  /** The level a `baseline` series is measured from; its first value when absent. */
  baseValue?: number;
  /** The indices of a `waterfall` that are running totals rather than changes. */
  totals?: readonly number[];
  /** The box a `pointfigure` series was built with; worked out from its columns when absent. */
  boxSize?: number;
  /**
   * This series' own x for each value, on a numeric or time x axis. A series with its own x is a
   * scatter: it need not line up with any other, and hovering picks out single points.
   */
  x?: readonly (number | Date | null)[];
  /** The chart's `kind` when absent. */
  kind?: ChartSeriesKind;
  /** The value axis it is measured against; `primary` when absent. */
  axis?: ChartValueAxis;
  /** The theme's `chartSeriesColor` for this series' position when absent. Unused by a pie. */
  color?: Color | null;
}

/**
 * A series as the chart holds it. The arrays are the chart's own copies: change them in place and
 * call `refresh()`, and nothing the caller passed in is ever touched.
 */
export interface ChartSeriesState extends ChartSeries {
  values: (number | null)[];
  ohlc?: (Ohlc | null)[];
  lower?: (number | null)[];
  x?: (number | Date | null)[];
}

/** Where an annotation sits along the category axis: an x on a numeric or time axis, else an index. */
export type ChartAt = number | Date;

/**
 * Something drawn on a chart that is not a series:
 *
 * - `line`: a level across the plot - a support, a resistance, a target - with its value read out
 *   on the axis and an optional `label`.
 * - `trend`: a line between two points, `extend`ed to the plot's edge if asked.
 * - `xrange`: a stretch of the category axis shaded across the plot - a session, an earnings week.
 * - `yrange`: a stretch of values shaded along the plot - a target zone.
 * - `marker`: an event at one period: an arrow `up` or `down`, a `dot`, or a `flag`, at `value` or
 *   at the top of the plot, with a `label`.
 *
 * Each takes a `color`, the theme's text color when absent.
 */
export type ChartAnnotation =
  | { type: "line"; value: number; axis?: ChartValueAxis; label?: string; dashed?: boolean; color?: Color | null }
  | {
      type: "trend";
      from: { at: ChartAt; value: number };
      to: { at: ChartAt; value: number };
      extend?: boolean;
      axis?: ChartValueAxis;
      label?: string;
      color?: Color | null;
    }
  | { type: "xrange"; from: ChartAt; to: ChartAt; label?: string; color?: Color | null }
  | { type: "yrange"; from: number; to: number; axis?: ChartValueAxis; label?: string; color?: Color | null }
  | {
      type: "marker";
      at: ChartAt;
      value?: number;
      axis?: ChartValueAxis;
      shape?: "up" | "down" | "dot" | "flag";
      label?: string;
      color?: Color | null;
    };

/** A volume profile: the volume traded at each price, as bars along the side of the plot. */
export interface ChartVolumeProfile {
  /** The `ohlc` series it is measured over. */
  prices: string;
  /**
   * Each period's volume: the name of a series in this chart, or the values themselves - from a
   * volume pane, say. Values are read again whenever this chart's data changes.
   */
  volume: string | readonly (number | null)[];
  /** How many price slices; 24 when absent. */
  bins?: number;
  /** How much of the plot's width the longest bar takes, 0..1; a quarter when absent. */
  width?: number;
  /**
   * Which end of the category axis the bars grow from: the right of a standing chart (the default)
   * or its left - and on a chart lying on its side, whose categories run down the page, the bottom
   * or the top.
   */
  side?: "left" | "right";
  color?: Color | null;
}

export interface ChartAppendOptions {
  /** The new category's label, on a category axis. */
  category?: string;
  /** The new x, shared by every series without its own, on a numeric or time axis. */
  x?: number | Date | null;
}

export interface ChartOptions extends ControlOptions {
  series?: readonly ChartSeries[];
  /** The x-axis labels, or the slice names of a pie, one per value. Numbered from 1 when absent. */
  categories?: readonly string[];
  chartType?: ChartType;
  orientation?: ChartOrientation;
  /** How a series with no `kind` of its own is drawn. */
  kind?: ChartSeriesKind;
  /** Pile bars on bars and areas on areas, rather than side by side and overlapping. */
  stacked?: boolean;
  /** The value axis: `linear`, or `log` for values spanning decades. */
  valueScale?: "linear" | "log";
  /** The second value axis, for the series with `axis: "secondary"`. */
  secondaryScale?: "linear" | "log";
  xScale?: ChartXScale;
  /** The x of every value, shared by the series that have none of their own. */
  xValues?: readonly (number | Date | null)[];
  /** Format an x for a tooltip; a date for a time axis, a number otherwise. */
  xFormat?: ((x: number) => string) | null;
  /** The hole in a donut, as a fraction of its radius. */
  holeRatio?: number;
  title?: string;
  /** Null shows the legend for a pie, and for a cartesian chart with more than one series. */
  showLegend?: boolean | null;
  showGrid?: boolean;
  /** Gridlines at the x axis' ticks too. Null draws them on a numeric or time axis and not on categories. */
  showXGrid?: boolean | null;
  /** Dots on every point of a line, step or area, not only the hovered one. */
  showMarkers?: boolean;
  lineWidth?: number;
  /** Fix an end of the value axis; null fits it to the data. */
  minimum?: number | null;
  maximum?: number | null;
  /** The same for the secondary axis. */
  secondaryMinimum?: number | null;
  secondaryMaximum?: number | null;
  /** The most gridlines an axis may be cut into. */
  tickCount?: number;
  /** Format a value for the tooltip; the axis step's precision when absent. */
  valueFormat?: ((value: number) => string) | null;
  /** Format a secondary-axis value; `valueFormat` when absent. */
  secondaryFormat?: ((value: number) => string) | null;
  /** A rising period on a financial chart; the theme's `chartTrendColor(true)` when absent. */
  upColor?: Color | null;
  /** A falling one; the theme's `chartTrendColor(false)` when absent. */
  downColor?: Color | null;
  /** Draw rising candles as outlines and falling ones filled, the old way. */
  hollowCandles?: boolean;
  /**
   * What colors a candle: whether it closed above its own `open` (the default), or above the
   * `previousClose`. With `hollowCandles`, the second is the modern hollow candle chart: hollow or
   * filled by the period's own move, green or red by the move from the period before.
   */
  candleColoring?: "open" | "previousClose";
  /** Lines, ranges and markers drawn on the plot; see `ChartAnnotation`. */
  annotations?: readonly ChartAnnotation[];
  /** Read each series' latest value out on its axis, with a line across at that level. */
  lastValueMarkers?: boolean;
  /** Volume by price along the side of the plot, from a price series and a volume series. */
  volumeProfile?: ChartVolumeProfile | null;
  /**
   * The category or date labels under the plot. A pane stacked above another that already shows
   * them turns them off; on by default.
   */
  showXAxis?: boolean;
  /**
   * On a time x axis, space the values one period apart and leave out the time between them - the
   * weekends and holidays of a trading chart. Date ticks still fall on calendar boundaries, each on
   * the first period at or after one.
   */
  skipGaps?: boolean;
  /**
   * Lines across the plot following the pointer, with the value under it read out on the value
   * axis and the category or date on the other - a trading chart's crosshair.
   */
  crosshair?: boolean;
  /** The tooltip of the hovered category or point; on by default. Off leaves the crosshair to say it. */
  showTooltip?: boolean;
  /**
   * The least room, in px, kept for the value labels beside the plot - and on the far side for the
   * secondary axis' labels, whether or not this chart has one. Charts stacked one above another
   * that share an x axis give each other the same widths, so their plots line up; null takes only
   * what the labels need.
   */
  valueAxisWidth?: number | null;
  secondaryAxisWidth?: number | null;
  /** Let the category axis be zoomed (ctrl+wheel, pinch, + and -) and panned (drag, shift+wheel). */
  zoomable?: boolean;
  /**
   * Keep only the newest this many values when appending: older ones are dropped from the front,
   * so a live chart scrolls rather than growing without end. Null keeps everything.
   */
  maxPoints?: number | null;
  font?: FontSpec | null;
  /** Raised with the category (or point) index on a click or Enter. */
  onCategoryClick?: (index: number) => void;
}

interface Ring {
  inner: number;
  outer: number;
  slices: PieSlice[];
}

/** A point of a scatter series, where it was last painted. */
interface PlacedPoint {
  series: number;
  index: number;
  at: Point;
}

/** Where everything went on the last paint, kept so a pointer can be mapped back to what it is over. */
type ChartLayout =
  | {
      type: "cartesian";
      plot: Rect;
      horizontal: boolean;
      /** The category-axis coordinate of each index, for a chart whose series share their x. */
      slots: number[];
      banded: boolean;
      count: number;
      /** Every point, for a scatter. Empty otherwise. */
      points: PlacedPoint[];
    }
  | { type: "radial"; center: Point; rings: Ring[] }
  | { type: "treemap"; tiles: (Rect | null)[] };

/** One value axis of a paint: its scale, how to place a value on it, and where bars grow from. */
interface ValueFrame {
  scale: ChartScale;
  /** Value-axis coordinate of `v`; NaN for a value a log axis cannot show. */
  v(value: number): number;
  base: number;
}

/**
 * The cartesian frame of one paint. Everything is worked out along two axes - `u` across the
 * categories and `v` along the values - and only turned into x and y at the end, by `pt`, which
 * is what lets one set of drawing code serve a chart standing up and one lying on its side.
 */
interface Frame {
  horizontal: boolean;
  plot: Rect;
  primary: ValueFrame;
  secondary: ValueFrame | null;
  /** The visible x axis on a numeric axis - zoomed, when it is - or null on categories. */
  x: ChartScale | null;
  count: number;
  banded: boolean;
  /** Where the category axis starts on screen, and how long it is. */
  catStart: number;
  catLen: number;
  /** Category-axis coordinate of series `s`, value `i`; NaN when it has no place. */
  u(s: number, i: number): number;
  pt(u: number, v: number): Point;
}

interface TooltipRow {
  text: string;
  color: Color;
}

interface TooltipContent {
  heading: string;
  rows: TooltipRow[];
}

const PAD = 8;
const GAP = 6;
const SWATCH = 10;
/** How far a hovered slice stands out from its pie. */
const POP = 6;
/** How near a scatter point the pointer has to be to pick it. */
const PICK = 24;
/** One notch of the wheel or one press of + or -. */
const ZOOM_STEP = 1.25;
const FULL_VIEW: ChartView = { from: 0, to: 1 };

const isValue = (v: number | null | undefined): v is number =>
  v !== null && v !== undefined && Number.isFinite(v);

const toNumber = (x: number | Date | null | undefined): number | null => {
  if (x === null || x === undefined) return null;
  const n = x instanceof Date ? x.getTime() : x;
  return Number.isFinite(n) ? n : null;
};

/** A series the chart owns: its arrays are its own, so appending to them cannot reach the caller's. */
type OwnSeries = ChartSeriesState;

const own = ({ x, ohlc, lower, values, totals, ...s }: ChartSeries): OwnSeries => ({
  ...s,
  ...(totals ? { totals: [...totals] } : {}),
  values: values ? [...values] : (ohlc ?? []).map((o) => (isOhlc(o) ? o.close : null)),
  ...(ohlc ? { ohlc: [...ohlc] } : {}),
  ...(lower ? { lower: [...lower] } : {}),
  ...(x ? { x: [...x] } : {}),
});

/**
 * A chart over a shared set of categories, or along a numeric, log or time x axis: lines, areas,
 * bars, steps and points against one or two linear or log value axes, standing up or on its side
 * - or a pie or donut.
 *
 * Series of different kinds mix in one plot: bars are grouped side by side within a category's
 * slot (or piled up when `stacked`), and everything else draws over them in series order. A series
 * with its own `x` is a scatter, and hovering one picks out single points rather than a whole
 * category. A pie takes its slices from the first series, and a second series becomes a ring
 * round it. Hovering, or arrowing through with the keyboard, shows a tooltip.
 *
 * **Live data.** `append` adds a value to every series at once, `appendPoint` one point to one
 * series, and `setValue` changes one in place; with `maxPoints` set the oldest fall off the front.
 * Each costs one repaint. The axes are worked out once per change rather than once per use, and
 * on a zoomed chart only what is in view is drawn, so a chart can be fed many times a second.
 *
 * The plot's gridlines and the series colors come from the theme (`drawChartPlot`,
 * `chartSeriesColor`); the marks themselves are the chart's own.
 */
export class Chart extends Control {
  #series: OwnSeries[] = [];
  #categories: string[] = [];
  #kind: ChartSeriesKind;
  #chartType: ChartType;
  #orientation: ChartOrientation;
  #stacked: boolean;
  #valueScale: "linear" | "log";
  #secondaryScale: "linear" | "log";
  #xScale: ChartXScale;
  #xValues: (number | Date | null)[];
  #view: ChartView = FULL_VIEW;
  #hovered = -1;
  #hoveredSeries = -1;
  #layout: ChartLayout | null = null;
  /** Bumped on every change to the data; the cached axes are keyed on it. */
  #version = 0;
  /** Where the pointer is over the chart, for the crosshair; null when it is not. */
  #pointer: Point | null = null;
  #annotations: ChartAnnotation[] = [];
  #cache = new Map<string, { key: string; value: unknown }>();

  title: string;
  showLegend: boolean | null;
  showGrid: boolean;
  showXGrid: boolean | null;
  showMarkers: boolean;
  lineWidth: number;
  holeRatio: number;
  minimum: number | null;
  maximum: number | null;
  secondaryMinimum: number | null;
  secondaryMaximum: number | null;
  tickCount: number;
  valueFormat: ((value: number) => string) | null;
  secondaryFormat: ((value: number) => string) | null;
  upColor: Color | null;
  downColor: Color | null;
  hollowCandles: boolean;
  candleColoring: "open" | "previousClose";
  lastValueMarkers: boolean;
  volumeProfile: ChartVolumeProfile | null;
  showXAxis: boolean;
  skipGaps: boolean;
  crosshair: boolean;
  showTooltip: boolean;
  valueAxisWidth: number | null;
  secondaryAxisWidth: number | null;
  xFormat: ((x: number) => string) | null;
  zoomable: boolean;
  maxPoints: number | null;
  font: FontSpec | null;

  constructor(options: ChartOptions = {}) {
    super({ width: 480, height: 280, tabStop: true, ...options });
    this.#kind = options.kind ?? "line";
    this.#chartType = options.chartType ?? "cartesian";
    this.#orientation = options.orientation ?? "vertical";
    this.#stacked = options.stacked ?? false;
    this.#valueScale = options.valueScale ?? "linear";
    this.#secondaryScale = options.secondaryScale ?? "linear";
    this.#xScale = options.xScale ?? "category";
    this.#xValues = [...(options.xValues ?? [])];
    this.title = options.title ?? "";
    this.showLegend = options.showLegend ?? null;
    this.showGrid = options.showGrid ?? true;
    this.showXGrid = options.showXGrid ?? null;
    this.showMarkers = options.showMarkers ?? false;
    this.lineWidth = options.lineWidth ?? 2;
    this.holeRatio = options.holeRatio ?? 0.55;
    this.minimum = options.minimum ?? null;
    this.maximum = options.maximum ?? null;
    this.secondaryMinimum = options.secondaryMinimum ?? null;
    this.secondaryMaximum = options.secondaryMaximum ?? null;
    this.tickCount = options.tickCount ?? 5;
    this.valueFormat = options.valueFormat ?? null;
    this.secondaryFormat = options.secondaryFormat ?? null;
    this.upColor = options.upColor ?? null;
    this.downColor = options.downColor ?? null;
    this.hollowCandles = options.hollowCandles ?? false;
    this.candleColoring = options.candleColoring ?? "open";
    this.#annotations = [...(options.annotations ?? [])];
    this.lastValueMarkers = options.lastValueMarkers ?? false;
    this.volumeProfile = options.volumeProfile ?? null;
    this.showXAxis = options.showXAxis ?? true;
    this.skipGaps = options.skipGaps ?? false;
    this.crosshair = options.crosshair ?? false;
    this.showTooltip = options.showTooltip ?? true;
    this.valueAxisWidth = options.valueAxisWidth ?? null;
    this.secondaryAxisWidth = options.secondaryAxisWidth ?? null;
    this.xFormat = options.xFormat ?? null;
    this.zoomable = options.zoomable ?? false;
    this.maxPoints = options.maxPoints ?? null;
    this.font = options.font ?? null;
    this.series = options.series ?? [];
    this.categories = options.categories ?? [];
    if (options.onCategoryClick) this.on("itemactivate", options.onCategoryClick);

    this.on("pointermove", (e) => this.#onPointer(e));
    this.on("pointerdown", (e) => this.#onPointer(e));
    this.on("pointerleave", () => {
      if (this.#pointer) {
        this.#pointer = null;
        this.invalidate();
      }
      if (!this.focused) this.hoveredIndex = -1;
    });
    this.on("click", (e) => this.#onClick(e));
    this.on("doubleclick", (e) => this.#onDoubleClick(e));
    this.on("wheel", (e) => this.#onWheel(e));
    this.on("keydown", (e) => this.#onKey(e));
    this.on("blur", () => (this.hoveredIndex = -1));
  }

  // --- data ---------------------------------------------------------------

  /**
   * The series, as the chart holds them. Setting copies what it is given, so the caller's arrays
   * are never changed by an `append`. Change one of these in place and call `refresh()`.
   */
  get series(): readonly ChartSeriesState[] {
    return this.#series;
  }
  set series(v: readonly ChartSeries[]) {
    this.#series = v.map(own);
    this.#changed();
  }

  get categories(): readonly string[] {
    return this.#categories;
  }
  set categories(v: readonly string[]) {
    this.#categories = [...v];
    this.#changed();
  }

  get xValues(): readonly (number | Date | null)[] {
    return this.#xValues;
  }
  set xValues(v: readonly (number | Date | null)[]) {
    this.#xValues = [...v];
    this.#changed();
  }

  get annotations(): readonly ChartAnnotation[] {
    return this.#annotations;
  }
  set annotations(v: readonly ChartAnnotation[]) {
    this.#annotations = [...v];
    this.invalidate();
  }

  /** Add an annotation; returns it, so it can be taken away again with `removeAnnotation`. */
  addAnnotation<T extends ChartAnnotation>(annotation: T): T {
    this.#annotations.push(annotation);
    this.invalidate();
    return annotation;
  }

  removeAnnotation(annotation: ChartAnnotation): boolean {
    const i = this.#annotations.indexOf(annotation);
    if (i < 0) return false;
    this.#annotations.splice(i, 1);
    this.invalidate();
    return true;
  }

  /** Add a series, or replace the one with the same name. */
  setSeries(series: ChartSeries): void {
    const i = this.#series.findIndex((s) => s.name === series.name);
    if (i >= 0) this.#series[i] = own(series);
    else this.#series.push(own(series));
    this.#changed();
  }

  removeSeries(name: string): boolean {
    const i = this.#series.findIndex((s) => s.name === name);
    if (i < 0) return false;
    this.#series.splice(i, 1);
    this.#changed();
    return true;
  }

  /**
   * Add one value to every series that shares the category axis - the next reading of a live
   * chart. `values` is one per series in order, or keyed by series name; a series left out gets a
   * gap. A series with an `x` of its own is a scatter and is left alone; use `appendPoint` for it.
   * With `maxPoints` set, the oldest values fall off the front.
   */
  append(
    values: readonly (number | Ohlc | ChartRange | null)[] | Readonly<Record<string, number | Ohlc | ChartRange | null>>,
    options: ChartAppendOptions = {},
  ): void {
    const byName = !Array.isArray(values);
    this.#series.forEach((s, i) => {
      if (s.x) return;
      const v = byName
        ? (values as Record<string, number | Ohlc | ChartRange | null>)[s.name]
        : (values as readonly (number | Ohlc | ChartRange | null)[])[i];
      this.#push(s, v ?? null);
    });
    if (options.category !== undefined) this.#categories.push(options.category);
    if (options.x !== undefined) this.#xValues.push(options.x);
    this.#trim();
    this.#changed();
  }

  /** Add one point to a scatter series - one with an `x` of its own. */
  appendPoint(series: string, x: number | Date | null, value: number | null): void {
    const s = this.#series.find((k) => k.name === series);
    if (!s) return;
    (s.x ??= []).push(x);
    s.values.push(value);
    this.#trim();
    this.#changed();
  }

  /**
   * Change one value in place - or one period's prices, on a financial series, which is how the
   * candle still forming at the end of a live chart is kept up to date. False when there is no
   * such series or value.
   */
  setValue(series: string, index: number, value: number | Ohlc | ChartRange | null): boolean {
    const s = this.#series.find((k) => k.name === series);
    if (!s || index < 0 || index >= s.values.length) return false;
    if (isOhlc(value) || s.ohlc) {
      const ohlc = (s.ohlc ??= s.values.map(() => null));
      ohlc[index] = isOhlc(value) ? value : null;
    }
    if (isRange(value) || s.lower) {
      const lower = (s.lower ??= s.values.map(() => null));
      lower[index] = isRange(value) ? value.lower : null;
    }
    s.values[index] = isOhlc(value) ? value.close : isRange(value) ? value.upper : (value as number | null);
    this.#changed();
    return true;
  }

  /** One value onto the end of a series: prices onto its `ohlc` too, and their close onto its values. */
  #push(s: OwnSeries, v: number | Ohlc | ChartRange | null): void {
    if (isOhlc(v) || s.ohlc) {
      const ohlc = (s.ohlc ??= s.values.map(() => null));
      ohlc.push(isOhlc(v) ? v : null);
    }
    if (isRange(v) || s.lower) {
      const lower = (s.lower ??= s.values.map(() => null));
      lower.push(isRange(v) ? v.lower : null);
    }
    s.values.push(isOhlc(v) ? v.close : isRange(v) ? v.upper : typeof v === "number" ? v : null);
  }

  /** Repaint after changing the data in place, through the arrays `series` hands out. */
  refresh(): void {
    this.#changed();
  }

  /** Drop the oldest values past `maxPoints`, keeping the hovered value where it was. */
  #trim(): void {
    const max = this.maxPoints;
    if (max === null || !(max > 0)) return;
    const cut = <T>(a: T[]): number => {
      const over = a.length - max;
      if (over > 0) a.splice(0, over);
      return Math.max(0, over);
    };
    let shared = 0;
    for (const s of this.#series) {
      if (s.ohlc) cut(s.ohlc);
      if (s.lower) cut(s.lower);
      if (s.x) {
        const dropped = cut(s.values);
        cut(s.x);
        if (this.#hoveredSeries >= 0 && this.#series[this.#hoveredSeries] === s) this.#shiftHover(dropped);
      } else {
        shared = Math.max(shared, cut(s.values));
      }
    }
    shared = Math.max(shared, cut(this.#categories), cut(this.#xValues));
    if (this.#hoveredSeries < 0) this.#shiftHover(shared);
  }

  #shiftHover(by: number): void {
    if (by <= 0 || this.#hovered < 0) return;
    this.#hovered -= by;
    if (this.#hovered < 0) {
      this.#hovered = -1;
      this.#hoveredSeries = -1;
      this.emit("selectionchanged");
    }
  }

  #changed(): void {
    this.#version++;
    this.#clampHover();
    this.emit("change");
    this.invalidate();
  }

  /** `compute`, or what it gave last time if nothing it depends on has changed since. */
  #memo<T>(name: string, key: string, compute: () => T): T {
    const full = `${this.#version}|${key}`;
    const hit = this.#cache.get(name);
    if (hit && hit.key === full) return hit.value as T;
    const value = compute();
    this.#cache.set(name, { key: full, value });
    return value;
  }

  // --- configuration ------------------------------------------------------

  get kind(): ChartSeriesKind {
    return this.#kind;
  }
  set kind(v: ChartSeriesKind) {
    this.#kind = v;
    this.invalidate();
  }

  get chartType(): ChartType {
    return this.#chartType;
  }
  set chartType(v: ChartType) {
    this.#chartType = v;
    this.#clampHover();
    this.invalidate();
  }

  get orientation(): ChartOrientation {
    return this.#orientation;
  }
  set orientation(v: ChartOrientation) {
    this.#orientation = v;
    this.invalidate();
  }

  get stacked(): boolean {
    return this.#stacked;
  }
  set stacked(v: boolean) {
    this.#stacked = v;
    this.invalidate();
  }

  get valueScale(): "linear" | "log" {
    return this.#valueScale;
  }
  set valueScale(v: "linear" | "log") {
    this.#valueScale = v;
    this.invalidate();
  }

  get secondaryScale(): "linear" | "log" {
    return this.#secondaryScale;
  }
  set secondaryScale(v: "linear" | "log") {
    this.#secondaryScale = v;
    this.invalidate();
  }

  get xScale(): ChartXScale {
    return this.#xScale;
  }
  set xScale(v: ChartXScale) {
    this.#xScale = v;
    this.#view = FULL_VIEW;
    this.#clampHover();
    this.invalidate();
  }

  /** The `ChartLink` this chart moves with, or null. */
  get link(): ChartLink | null {
    return linkOf(this);
  }

  /** True for a pie or a donut. */
  get radial(): boolean {
    return this.#chartType === "pie" || this.#chartType === "donut";
  }

  /**
   * True when hovering picks single points rather than whole categories: a cartesian chart on a
   * numeric x axis where any series has an `x` of its own.
   */
  get scatter(): boolean {
    return this.#cartesian && !this.#indexed && this.#series.some((s) => s.x !== undefined);
  }

  get #cartesian(): boolean {
    return this.#chartType === "cartesian";
  }

  /**
   * True when values sit one slot apart by index rather than at an x of their own: a category axis,
   * or a time axis with `skipGaps`.
   */
  get #indexed(): boolean {
    return this.#xScale === "category" || (this.#xScale === "time" && this.skipGaps);
  }

  /** True when any series is measured against the secondary axis. */
  get hasSecondaryAxis(): boolean {
    return this.#cartesian && this.#series.some((s) => s.axis === "secondary");
  }

  /** How many categories there are: the longest of the labels and every series. */
  get categoryCount(): number {
    let n = this.#categories.length;
    for (const s of this.#series) n = Math.max(n, s.values.length);
    return n;
  }

  /**
   * The label of category `index`: its name, else its x formatted for a numeric axis, else its
   * 1-based number.
   */
  categoryLabel(index: number): string {
    const name = this.#categories[index];
    if (name !== undefined) return name;
    if (this.#xScale !== "category") {
      const x = toNumber(this.#xValues[index]);
      if (x !== null) return this.formatX(x);
    }
    return String(index + 1);
  }

  /** The x of value `index` of series `s` on a numeric axis, or null when it has none. */
  xOf(s: number, index: number): number | null {
    const own = this.#series[s]?.x;
    return toNumber(own ? own[index] : this.#xValues[index]);
  }

  kindOf(series: ChartSeries): ChartSeriesKind {
    return series.kind ?? this.#kind;
  }

  /** Whether `series` piles onto the ones before it: a bar or an area, on a stacked chart. */
  stacks(series: ChartSeries): boolean {
    if (!this.#stacked) return false;
    const kind = this.kindOf(series);
    return kind === "bar" || kind === "area";
  }

  #isSecondary(series: ChartSeries): boolean {
    return series.axis === "secondary";
  }

  /**
   * Where every series starts and ends at every category, with stacking applied. Each value axis
   * stacks on its own: a series never piles onto one measured in different units.
   */
  stack(): Stack {
    return this.#memo("stack", `${this.#stacked}|${this.#kind}`, () => {
      const count = this.categoryCount;
      const primary = stackValues(this.#series, this.#series.map((s) => this.stacks(s) && !this.#isSecondary(s)), count);
      const secondary = stackValues(this.#series, this.#series.map((s) => this.stacks(s) && this.#isSecondary(s)), count);
      return {
        from: this.#series.map((s, i) => (this.#isSecondary(s) ? secondary.from[i]! : primary.from[i]!)),
        to: this.#series.map((s, i) => (this.#isSecondary(s) ? secondary.to[i]! : primary.to[i]!)),
      };
    });
  }

  /** The level a `baseline` series is measured from: its `baseValue`, else its first value. */
  baseValueOf(series: ChartSeries): number {
    if (series.baseValue !== undefined && Number.isFinite(series.baseValue)) return series.baseValue;
    return (series.values ?? []).find(isValue) ?? 0;
  }

  /** A `waterfall` series' bars: where each floats from and to. */
  waterfallOf(series: ChartSeries): (WaterfallStep | null)[] {
    return waterfall(series.values ?? [], new Set(series.totals ?? []));
  }

  /**
   * The box a `pointfigure` series was built with: its `boxSize`, else the smallest step between
   * the levels its columns reach, which is a box or a whole number of them.
   */
  boxSizeOf(series: ChartSeries): number {
    if (series.boxSize !== undefined && series.boxSize > 0) return series.boxSize;
    let box = Infinity;
    for (const o of series.ohlc ?? []) {
      if (!isOhlc(o)) continue;
      const span = Math.abs(o.close - o.open);
      if (span > 0) box = Math.min(box, span);
    }
    return Number.isFinite(box) ? box : 1;
  }

  // --- axes ---------------------------------------------------------------

  /** A value axis, primary by default, with its `minimum` and `maximum` applied. */
  valueAxis(secondary = false): ChartScale {
    const scaleKind = secondary ? this.#secondaryScale : this.#valueScale;
    const min = secondary ? this.secondaryMinimum : this.minimum;
    const max = secondary ? this.secondaryMaximum : this.maximum;
    const key = `${secondary}|${scaleKind}|${min}|${max}|${this.tickCount}|${this.#stacked}|${this.#kind}`;
    return this.#memo(secondary ? "secondary" : "primary", key, () => this.#computeValueAxis(secondary, scaleKind, min, max));
  }

  #computeValueAxis(secondary: boolean, scaleKind: "linear" | "log", minimum: number | null, maximum: number | null): ChartScale {
    const mine = this.#series.map((s, i) => ({ s, i })).filter(({ s }) => this.#isSecondary(s) === secondary);
    const stack = this.#stacked ? this.stack() : null;
    // A financial series is measured by its lows and highs, which reach past its closes.
    const measured = mine.flatMap<{ values: readonly (number | null)[] }>(({ s, i }) => {
      const kind = this.kindOf(s);
      if (kind === "pointfigure" && s.ohlc) {
        // The top column's marks reach a box above its level.
        const box = this.boxSizeOf(s);
        return [{ values: s.ohlc.map((o) => (isOhlc(o) ? o.low : null)) }, { values: s.ohlc.map((o) => (isOhlc(o) ? o.high + box : null)) }];
      }
      if (kind === "waterfall") {
        const steps = this.waterfallOf(s);
        return [{ values: steps.map((st) => st?.from ?? null) }, { values: steps.map((st) => st?.to ?? null) }];
      }
      if (kind === "range" && s.lower) return [s, { values: s.lower }];
      if (kind === "baseline") return [s, { values: [this.baseValueOf(s)] }];
      if (s.ohlc && isFinancialKind(this.kindOf(s))) {
        return [{ values: s.ohlc.map((o) => (isOhlc(o) ? o.low : null)) }, { values: s.ohlc.map((o) => (isOhlc(o) ? o.high : null)) }];
      }
      if (s.lower && this.kindOf(s) === "band") return [s, { values: s.lower }];
      return stack && this.stacks(s) ? [{ values: stack.from[i]! }, { values: stack.to[i]! }] : [s];
    });
    if (scaleKind === "log") {
      const positive = measured.map((m) => ({ values: m.values.map((v) => (isValue(v) && v > 0 ? v : null)) }));
      const extent = valueExtent(positive, false);
      return logScale(minimum ?? extent?.lo ?? 1, maximum ?? extent?.hi ?? 10, this.tickCount + 1);
    }
    const anchored = mine.some(({ s }) => {
      const kind = this.kindOf(s);
      return kind === "bar" || kind === "area" || kind === "waterfall";
    });
    const extent = valueExtent(measured, anchored) ?? { lo: 0, hi: 1 };
    const scale = linearScale(minimum ?? extent.lo, maximum ?? extent.hi, this.tickCount);
    if (minimum === null && maximum === null) return scale;
    // A fixed end is kept exactly; only the free end rounds out to a step.
    const lo = minimum ?? scale.min;
    const hi = maximum ?? scale.max;
    const keep = scale.ticks.map((t, i) => [t, scale.labels[i]!] as const).filter(([t]) => t >= lo - 1e-9 && t <= hi + 1e-9);
    return { ...scale, min: lo, max: hi, ticks: keep.map(([t]) => t), labels: keep.map(([, l]) => l) };
  }

  /** The whole numeric x axis, ignoring any zoom, or null on a category axis. */
  xAxis(): ChartScale | null {
    if (this.#xScale === "category") return null;
    return this.#memo("x", `${this.#xScale}|${this.tickCount}`, () => {
      let lo = Infinity;
      let hi = -Infinity;
      this.#series.forEach((s, si) => {
        for (let i = 0; i < s.values.length; i++) {
          const x = this.xOf(si, i);
          if (x === null || (this.#xScale === "log" && x <= 0)) continue;
          if (x < lo) lo = x;
          if (x > hi) hi = x;
        }
      });
      if (this.#xScale === "time") return timeScale(lo, hi, this.tickCount + 1);
      if (this.#xScale === "log") return logScale(lo, hi, this.tickCount + 1);
      return linearScale(lo, hi, this.tickCount + 1);
    });
  }

  /** The part of the x axis in view: the whole of it unless zoomed. Null on a category axis. */
  visibleXAxis(): ChartScale | null {
    const full = this.xAxis();
    if (!full) return null;
    const { from, to } = this.#view;
    return this.#memo("xview", `${this.#xScale}|${this.tickCount}|${from}|${to}`, () => viewScale(full, from, to, this.tickCount + 1));
  }

  // --- zoom ---------------------------------------------------------------

  /** The visible part of the category axis, as fractions of the whole. */
  get view(): ChartView {
    return this.#view;
  }

  /** True when part of the category axis is out of view. */
  get zoomed(): boolean {
    return this.#view.from > 0 || this.#view.to < 1;
  }

  /** Show only `from`..`to` of the category axis, as fractions of the whole. */
  zoomTo(from: number, to: number): void {
    const a = Math.max(0, Math.min(1, Math.min(from, to)));
    const b = Math.max(0, Math.min(1, Math.max(from, to)));
    const span = Math.max(this.#minSpan(), b - a);
    const start = Math.min(1 - span, a);
    this.#setView({ from: start, to: start + span });
  }

  /** Back to the whole axis. */
  resetZoom(): void {
    this.#setView(FULL_VIEW);
  }

  /**
   * Zoom about a point in the chart's own coordinates - in when `factor` is above 1. What is under
   * the point stays under it. False when nothing changed: not zoomable, a pie, or at a limit -
   * which lets a pinch carry on to a viewer further out.
   */
  override zoomBy(factor: number, at: Point): boolean {
    if (!this.zoomable || !this.#cartesian || factor === 1) return false;
    const before = this.#view;
    this.#setView(zoomView(before, factor, this.#fractionAt(at), this.#minSpan()));
    return this.#view.from !== before.from || this.#view.to !== before.to;
  }

  /**
   * Slide a zoomed chart along the category axis by a drag of `dx`, `dy` px, and report how much
   * was used, so a drag past either end hands the rest to whatever scrolls outside the chart.
   */
  override panBy(dx: number, dy: number): Point {
    const l = this.#layout;
    if (!this.zoomable || !this.zoomed || !l || l.type !== "cartesian") return { x: 0, y: 0 };
    const along = l.horizontal ? dy : dx;
    const length = l.horizontal ? l.plot.height : l.plot.width;
    if (!(length > 0)) return { x: 0, y: 0 };
    const span = this.#view.to - this.#view.from;
    const before = this.#view.from;
    this.#setView(panView(this.#view, (along / length) * span));
    const used = ((this.#view.from - before) / span) * length;
    return l.horizontal ? { x: 0, y: used } : { x: used, y: 0 };
  }

  /** A zoomed chart pans under a mouse drag as it does under a finger. */
  override get pansWithMouse(): boolean {
    return this.zoomable && this.zoomed;
  }

  #setView(v: ChartView): void {
    if (v.from === this.#view.from && v.to === this.#view.to) return;
    this.#view = v.from <= 0 && v.to >= 1 ? FULL_VIEW : v;
    this.emit("zoom");
    this.invalidate();
  }

  /** The narrowest view: two categories, or a ten-thousandth of a numeric axis. */
  #minSpan(): number {
    if (!this.#indexed) return 1e-4;
    return Math.min(1, 2 / Math.max(1, this.categoryCount));
  }

  /** Where a local point falls along the category axis, as a fraction of the whole axis. */
  #fractionAt(p: Point): number {
    const l = this.#layout;
    const { from, to } = this.#view;
    if (!l || l.type !== "cartesian") return (from + to) / 2;
    const start = l.horizontal ? l.plot.y : l.plot.x;
    const length = l.horizontal ? l.plot.height : l.plot.width;
    if (!(length > 0)) return (from + to) / 2;
    const t = Math.min(1, Math.max(0, ((l.horizontal ? p.y : p.x) - start) / length));
    return from + t * (to - from);
  }

  // --- hover --------------------------------------------------------------

  /** The category (or, on a scatter, the point) picked out by the pointer or the keyboard, or -1. */
  get hoveredIndex(): number {
    return this.#hovered;
  }
  set hoveredIndex(v: number) {
    const series = this.scatter ? Math.max(0, this.#hoveredSeries) : -1;
    this.#setHover(series, v);
  }

  /** The series of the hovered point on a scatter; -1 when a whole category is picked out. */
  get hoveredSeries(): number {
    return this.#hovered < 0 ? -1 : this.#hoveredSeries;
  }

  /** Pick out point `index` of series `series` on a scatter, or category `index` with `series` -1. */
  hoverPoint(series: number, index: number): void {
    this.#setHover(series, index);
  }

  #setHover(series: number, index: number): void {
    const limit = series >= 0 ? (this.#series[series]?.values.length ?? 0) : this.categoryCount;
    const next = index >= 0 && index < limit ? Math.floor(index) : -1;
    const nextSeries = next < 0 ? -1 : series;
    if (next === this.#hovered && nextSeries === this.#hoveredSeries) return;
    this.#hovered = next;
    this.#hoveredSeries = nextSeries;
    this.emit("selectionchanged");
    this.invalidate();
  }

  #clampHover(): void {
    const s = this.#hoveredSeries;
    const limit = s >= 0 ? (this.#series[s]?.values.length ?? 0) : this.categoryCount;
    if (this.#hovered >= limit || (s >= 0 && !this.scatter)) this.#setHover(-1, -1);
  }

  /**
   * What is under a local point: a category, a slice, or on a scatter the nearest point within
   * reach. `series` is -1 unless a single point was picked. Index -1 when it is over nothing.
   */
  pickAt(x: number, y: number): { series: number; index: number } {
    const none = { series: -1, index: -1 };
    const l = this.#layout;
    if (!l) return none;
    if (l.type === "treemap") {
      const index = l.tiles.findIndex((t) => t !== null && x >= t.x && x < t.x + t.width && y >= t.y && y < t.y + t.height);
      return { series: -1, index };
    }
    if (l.type === "radial") {
      for (let i = 0; i < l.rings.length; i++) {
        const ring = l.rings[i]!;
        const outer = ring.outer + (i === l.rings.length - 1 ? POP : 0);
        const hit = sliceAt({ x, y }, l.center, ring.inner, outer, ring.slices);
        if (hit) return { series: -1, index: hit.index };
      }
      return none;
    }
    const p = l.plot;
    if (x < p.x - PAD || x > p.x + p.width + PAD || y < p.y - PAD || y > p.y + p.height + PAD) return none;
    if (l.points.length > 0) {
      let best: PlacedPoint | null = null;
      let bestD = PICK;
      for (const pt of l.points) {
        const d = Math.hypot(pt.at.x - x, pt.at.y - y);
        if (d < bestD) {
          bestD = d;
          best = pt;
        }
      }
      return best ? { series: best.series, index: best.index } : none;
    }
    if (l.count === 0) return none;
    if (this.#indexed) {
      return { series: -1, index: nearestCategory(this.#fractionAt({ x, y }), l.count, 0, 1, l.banded) };
    }
    const along = l.horizontal ? y : x;
    let index = -1;
    let bestD = Infinity;
    l.slots.forEach((u, i) => {
      const d = Math.abs(u - along);
      if (Number.isFinite(d) && d < bestD) {
        bestD = d;
        index = i;
      }
    });
    return { series: -1, index };
  }

  /** The category under a local point, or -1. On a scatter, the index of the nearest point. */
  categoryAt(x: number, y: number): number {
    return this.pickAt(x, y).index;
  }

  // --- formatting ---------------------------------------------------------

  /** The text of a value as the tooltip shows it, measured against the primary axis or the secondary. */
  formatValue(v: number, secondary = false): string {
    const custom = secondary ? (this.secondaryFormat ?? this.valueFormat) : this.valueFormat;
    if (custom) return custom(v);
    if ((secondary ? this.#secondaryScale : this.#valueScale) === "log") return String(Number(v.toPrecision(4)));
    const step = this.#cartesian ? this.valueAxis(secondary).step : 1;
    const decimals = Math.min(10, decimalsFor(step) + 2);
    return String(Number(v.toFixed(decimals)));
  }

  /** The text of an x as the tooltip shows it. */
  formatX(x: number): string {
    if (this.xFormat) return this.xFormat(x);
    if (this.#xScale === "time") return formatMoment(x);
    return String(Number(x.toPrecision(6)));
  }

  /** What the tooltip says about the picked-out category or point, as one line; empty when none is. */
  hoverSummary(): string {
    const content = this.#tooltipContent((i) => ({ r: 0, g: 0, b: 0, a: i }));
    return content ? `${content.heading}: ${content.rows.map((r) => r.text).join(", ")}` : "";
  }

  // --- input --------------------------------------------------------------

  #onPointer(e: MouseEventArgs): void {
    const hit = this.pickAt(e.x, e.y);
    this.#setHover(hit.series, hit.index);
    if (this.crosshair && this.#cartesian) {
      this.#pointer = { x: e.x, y: e.y };
      this.invalidate();
    }
  }

  #onClick(e: MouseEventArgs): void {
    const i = this.pickAt(e.x, e.y).index;
    if (i >= 0) this.emit("itemactivate", i);
  }

  #onDoubleClick(e: MouseEventArgs): void {
    if (!this.zoomable || !this.zoomed) return;
    this.resetZoom();
    e.handled = true;
  }

  /**
   * Ctrl+wheel zooms about the pointer, as a trackpad pinch does - browsers report one as the
   * other. Shift+wheel, or a sideways swipe, pans a zoomed chart. A plain wheel is left alone, so a
   * chart in a scrolling page does not take the page's scrolling away from it.
   */
  #onWheel(e: WheelEventArgs): void {
    if (!this.zoomable || !this.#cartesian) return;
    if (e.ctrlKey || e.metaKey) {
      if (this.zoomBy(e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP, { x: e.x, y: e.y })) e.handled = true;
      return;
    }
    const sideways = e.shiftKey ? e.deltaY : e.deltaX;
    if (sideways === 0 || !this.zoomed) return;
    const used = this.panBy(sideways, sideways);
    if (used.x !== 0 || used.y !== 0) e.handled = true;
  }

  #onKey(e: KeyEventArgs): void {
    if (this.zoomable && this.#cartesian && this.#onZoomKey(e)) return;
    if (this.scatter) {
      this.#onScatterKey(e);
      return;
    }
    const n = this.categoryCount;
    if (n === 0) return;
    const at = this.#hovered;
    let next = at;
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        next = at < 0 ? 0 : Math.min(n - 1, at + 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        next = at < 0 ? n - 1 : Math.max(0, at - 1);
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = n - 1;
        break;
      case "Enter":
      case " ":
        if (at >= 0) this.emit("itemactivate", at);
        e.handled = true;
        return;
      case "Escape":
        if (at < 0) return;
        next = -1;
        break;
      default:
        return;
    }
    this.#setHover(-1, next);
    this.#keepInView(next);
    e.handled = true;
  }

  /** + and - zoom about the hovered category, or the middle; 0 shows everything. */
  #onZoomKey(e: KeyEventArgs): boolean {
    const l = this.#layout;
    let at: Point = { x: this.width / 2, y: this.height / 2 };
    if (l?.type === "cartesian") {
      at = { x: l.plot.x + l.plot.width / 2, y: l.plot.y + l.plot.height / 2 };
      const u = this.#hovered >= 0 && this.#hoveredSeries < 0 ? l.slots[this.#hovered] : undefined;
      if (u !== undefined && Number.isFinite(u)) at = l.horizontal ? { x: at.x, y: u } : { x: u, y: at.y };
    }
    switch (e.key) {
      case "+":
      case "=":
        this.zoomBy(ZOOM_STEP, at);
        break;
      case "-":
      case "_":
        this.zoomBy(1 / ZOOM_STEP, at);
        break;
      case "0":
        this.resetZoom();
        break;
      default:
        return false;
    }
    e.handled = true;
    return true;
  }

  /** Slide a zoomed category axis just far enough to bring category `index` into view. */
  #keepInView(index: number): void {
    if (index < 0 || !this.zoomed || !this.#indexed) return;
    const count = this.categoryCount;
    const banded = this.#layout?.type === "cartesian" && this.#layout.banded;
    const f = categoryX(index, count, 0, 1, banded);
    const margin = banded ? 0.5 / count : 0;
    const { from, to } = this.#view;
    if (f - margin < from) this.#setView(panView(this.#view, f - margin - from));
    else if (f + margin > to) this.#setView(panView(this.#view, f + margin - to));
  }

  /** Left and right walk a scatter series' points; up and down move to the next series. */
  #onScatterKey(e: KeyEventArgs): void {
    const count = this.#series.length;
    if (count === 0) return;
    let s = this.#hovered < 0 ? 0 : this.#hoveredSeries;
    let i = this.#hovered;
    const len = (k: number) => this.#series[k]?.values.length ?? 0;
    switch (e.key) {
      case "ArrowRight":
        i = i < 0 ? 0 : Math.min(len(s) - 1, i + 1);
        break;
      case "ArrowLeft":
        i = i < 0 ? len(s) - 1 : Math.max(0, i - 1);
        break;
      case "ArrowDown":
        s = (s + 1) % count;
        i = Math.min(Math.max(0, i), len(s) - 1);
        break;
      case "ArrowUp":
        s = (s - 1 + count) % count;
        i = Math.min(Math.max(0, i), len(s) - 1);
        break;
      case "Home":
        i = 0;
        break;
      case "End":
        i = len(s) - 1;
        break;
      case "Enter":
      case " ":
        if (this.#hovered >= 0) this.emit("itemactivate", this.#hovered);
        e.handled = true;
        return;
      case "Escape":
        if (this.#hovered < 0) return;
        i = -1;
        break;
      default:
        return;
    }
    this.#setHover(s, i);
    e.handled = true;
  }

  // --- paint --------------------------------------------------------------

  #dim(c: Color): Color {
    return this.effectivelyEnabled ? c : withAlpha(c, c.a * 0.35);
  }

  #colorOf(theme: Theme, index: number): Color {
    return this.#dim(this.#series[index]?.color ?? theme.chartSeriesColor(index));
  }

  #categoryColor(theme: Theme, index: number): Color {
    return this.#dim(theme.chartSeriesColor(index));
  }

  #showsLegend(): boolean {
    return this.showLegend ?? (this.#chartType === "treemap" ? false : this.radial || this.#series.length > 1);
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const pal = theme.palette;
    const font = this.font ?? theme.metrics.font;
    const lineH = rc.lineHeight(font);
    const ink = this.effectivelyEnabled ? pal.text : pal.textDisabled;
    const r = this.localRect;

    let top = r.y + PAD;
    let bottom = r.y + r.height - PAD;
    if (this.title) {
      rc.drawText(this.title, r.x + PAD, top, { ...font, weight: 600 }, ink, { maxWidth: r.width - PAD * 2, baseline: "top" });
      top += lineH + GAP;
    }
    if (this.#showsLegend()) {
      bottom -= lineH;
      this.#paintLegend(rc, theme, font, { x: r.x + PAD, y: bottom, width: r.width - PAD * 2, height: lineH }, ink);
      bottom -= GAP;
    }

    const area: Rect = { x: r.x + PAD, y: top, width: r.width - PAD * 2, height: bottom - top };
    if (this.#chartType === "treemap") this.#paintTreemap(rc, theme, font, area, r);
    else if (this.radial) this.#paintRadial(rc, theme, font, area, r);
    else this.#paintCartesian(rc, theme, font, area, r);

    if (this.focused && this.effectivelyEnabled) {
      rc.strokeRect(r, pal.focusRing, theme.metrics.focusRingWidth, theme.metrics.controlCornerRadius);
    }
  }

  #paintLegend(rc: RenderContext, theme: Theme, font: FontSpec, r: Rect, ink: Color): void {
    const entries = !this.#cartesian
      ? Array.from({ length: this.categoryCount }, (_, i) => ({ name: this.categoryLabel(i), color: this.#categoryColor(theme, i) }))
      : this.#series.map((s, i) => ({ name: s.name, color: isFinancialKind(this.kindOf(s)) ? this.#trend(theme, true) : this.#colorOf(theme, i) }));
    let x = r.x;
    const right = r.x + r.width;
    for (const entry of entries) {
      if (x >= right) return;
      rc.fillRect({ x, y: r.y + (r.height - SWATCH) / 2, width: SWATCH, height: SWATCH }, entry.color, 2);
      x += SWATCH + 4;
      x += rc.drawText(entry.name, x, r.y, font, ink, { maxWidth: Math.max(0, right - x), baseline: "top" }) + 14;
    }
  }

  // --- cartesian ----------------------------------------------------------

  /** The labels along the category axis, and where each sits as a fraction of it (null: by index). */
  #categoryTicks(x: ChartScale | null, count: number): { label: string; at: number | null; index: number }[] {
    if (x) return x.ticks.map((t, i) => ({ label: x.labels[i]!, at: scaleFraction(t, x), index: i }));
    if (this.#xScale === "time") {
      // Periods spaced evenly: calendar ticks, each on the first period at or after it.
      const { from, to } = this.#view;
      const first = Math.max(0, Math.floor(from * count));
      const last = Math.min(count - 1, Math.ceil(to * count) - 1);
      const key = `${this.tickCount}|${first}|${last}`;
      const ticks = this.#memo("periods", key, () =>
        periodTicks(Array.from({ length: count }, (_, i) => this.xOf(0, i)), first, last, this.tickCount + 1),
      );
      return ticks.map((t) => ({ label: t.label, at: null, index: t.index }));
    }
    return Array.from({ length: count }, (_, i) => ({ label: this.categoryLabel(i), at: null, index: i }));
  }

  #valueFrame(scale: ChartScale, plot: Rect, horizontal: boolean): ValueFrame {
    const v = (val: number): number => {
      const f = scaleFraction(val, scale);
      if (!Number.isFinite(f)) return Number.NaN;
      return horizontal ? plot.x + f * plot.width : plot.y + plot.height - f * plot.height;
    };
    const baseValue = scale.kind === "log" ? scale.min : Math.min(scale.max, Math.max(scale.min, 0));
    return { scale, v, base: v(baseValue) };
  }

  #frame(rc: RenderContext, font: FontSpec, area: Rect): Frame {
    const lineH = rc.lineHeight(font);
    const horizontal = this.#orientation === "horizontal";
    const primary = this.valueAxis();
    const secondary = this.hasSecondaryAxis ? this.valueAxis(true) : null;
    const x = this.#indexed ? null : this.visibleXAxis();
    const count = this.categoryCount;
    // Bars and candles each need a slot of their own, so categories become bands under them.
    const banded = !x && this.#series.some((s) => takesSlot(this.kindOf(s)));
    const widest = (labels: readonly string[]) => Math.max(0, ...labels.map((s) => rc.measureWidth(s, font)));
    const valueW = Math.max(widest(primary.labels), this.valueAxisWidth ?? 0);
    const secondW = Math.max(secondary ? widest(secondary.labels) : 0, this.secondaryAxisWidth ?? 0);
    const cats = this.#categoryTicks(x, count);
    const below = this.showXAxis ? lineH + Math.max(4, lineH / 2) : 4;

    let plot: Rect;
    if (!horizontal) {
      const lastCat = x && cats.length ? rc.measureWidth(cats[cats.length - 1]!.label, font) / 2 : 0;
      let insets = { left: valueW + GAP, right: Math.max(lineH / 2, lastCat, secondW > 0 ? secondW + GAP : 0) };
      // Linked charts take the room the widest-labeled of them needs, so their plots line up.
      const link = linkOf(this);
      if (link) insets = link.alignInsets(this, insets);
      const left = area.x + insets.left;
      const right = area.x + area.width - insets.right;
      const top = area.y + lineH / 2;
      plot = { x: left, y: top, width: Math.max(0, right - left), height: Math.max(0, area.y + area.height - below - top) };
    } else {
      const catW = this.showXAxis ? Math.min(widest(cats.map((c) => c.label)), area.width * 0.35) : 0;
      const left = area.x + catW + GAP;
      const lastValue = primary.labels.length ? rc.measureWidth(primary.labels[primary.labels.length - 1]!, font) / 2 : 0;
      const lastSecond = secondary?.labels.length ? rc.measureWidth(secondary.labels[secondary.labels.length - 1]!, font) / 2 : 0;
      const right = area.x + area.width - Math.max(lineH / 2, lastValue, lastSecond);
      const top = area.y + (secondary ? lineH + Math.max(4, lineH / 2) : 4);
      plot = { x: left, y: top, width: Math.max(0, right - left), height: Math.max(0, area.y + area.height - below - top) };
    }

    const catStart = horizontal ? plot.y : plot.x;
    const catLen = horizontal ? plot.height : plot.width;
    const { from, to } = this.#view;
    const span = to - from;
    const u = (s: number, i: number): number => {
      if (!x) return catStart + ((categoryX(i, count, 0, 1, banded) - from) / span) * catLen;
      const xv = this.xOf(s, i);
      return xv === null ? Number.NaN : catStart + scaleFraction(xv, x) * catLen;
    };
    return {
      horizontal,
      plot,
      primary: this.#valueFrame(primary, plot, horizontal),
      secondary: secondary ? this.#valueFrame(secondary, plot, horizontal) : null,
      x,
      count,
      banded,
      catStart,
      catLen,
      u,
      pt: (a, b) => (horizontal ? { x: b, y: a } : { x: a, y: b }),
    };
  }

  /** The value frame series `i` is measured against. */
  #axisOf(f: Frame, i: number): ValueFrame {
    const s = this.#series[i];
    return s && this.#isSecondary(s) && f.secondary ? f.secondary : f.primary;
  }

  #paintCartesian(rc: RenderContext, theme: Theme, font: FontSpec, area: Rect, bounds: Rect): void {
    const f = this.#frame(rc, font, area);
    const { plot, horizontal, count } = f;
    const scatter = this.scatter;
    const slots = scatter ? [] : Array.from({ length: count }, (_, i) => f.u(0, i));
    const points: PlacedPoint[] = [];
    if (scatter) {
      this.#series.forEach((s, si) => {
        const axis = this.#axisOf(f, si);
        for (let i = 0; i < s.values.length; i++) {
          const val = s.values[i];
          if (!isValue(val)) continue;
          const a = f.u(si, i);
          const b = axis.v(val);
          if (Number.isFinite(a) && Number.isFinite(b) && a >= f.catStart - PAD && a <= f.catStart + f.catLen + PAD) {
            points.push({ series: si, index: i, at: f.pt(a, b) });
          }
        }
      });
    }
    this.#layout = { type: "cartesian", plot, horizontal, slots, banded: f.banded, count, points };
    if (plot.width <= 0 || plot.height <= 0) return;

    const primary = f.primary;
    const zero = primary.scale.kind === "linear" && primary.scale.min <= 0 && primary.scale.max >= 0 ? primary.v(0) : null;
    const showX = this.showXGrid ?? this.#xScale !== "category";
    const crossLines = showX ? this.#categoryTicks(f.x, count).map((c) => (c.at === null ? f.u(0, c.index) : f.catStart + c.at * f.catLen)) : [];
    theme.drawChartPlot(rc, plot, {
      lines: horizontal ? "vertical" : "horizontal",
      gridLines: primary.scale.ticks.map((t) => primary.v(t)),
      crossLines: crossLines.filter((c) => Number.isFinite(c) && c >= f.catStart - 0.5 && c <= f.catStart + f.catLen + 0.5),
      baseline: zero,
      showGrid: this.showGrid,
      disabled: !this.effectivelyEnabled,
    });
    this.#paintAxisLabels(rc, theme, font, f, bounds);

    const stack = this.stack();
    // Unzoomed, the clip reaches a little past the plot so a dot on its edge is whole; zoomed, the
    // category axis is cut exactly at the plot so what is out of view stays out of the labels.
    const across = this.zoomed ? 0 : PAD;
    const clip: Rect = horizontal
      ? { x: plot.x - PAD, y: plot.y - across, width: plot.width + PAD * 2, height: plot.height + across * 2 }
      : { x: plot.x - across, y: plot.y - PAD, width: plot.width + across * 2, height: plot.height + PAD * 2 };
    rc.clipped(clip, () => {
      this.#paintRanges(rc, theme, font, f);
      this.#paintVolumeProfile(rc, theme, f);
      this.#paintBands(rc, theme, f);
      this.#paintBars(rc, theme, f, stack);
      this.#paintWaterfalls(rc, theme, f);
      this.#paintCandles(rc, theme, f);
      this.#paintBaselines(rc, theme, f);
      this.#paintKagi(rc, theme, f);
      this.#paintMarks(rc, theme, f, stack);
      this.#paintAnnotationLines(rc, theme, font, f);
    });
    this.#paintLevelReadouts(rc, theme, font, f, bounds);

    if (this.crosshair) this.#paintCrosshair(rc, theme, font, f, bounds);
    if (this.#hovered >= 0) this.#paintCartesianHover(rc, theme, font, f, stack, bounds);
  }

  #paintAxisLabels(rc: RenderContext, theme: Theme, font: FontSpec, f: Frame, bounds: Rect): void {
    const lineH = rc.lineHeight(font);
    const subtle = this.effectivelyEnabled ? theme.palette.textSubtle : theme.palette.textDisabled;
    const { plot, horizontal } = f;
    const belowY = plot.y + plot.height + Math.max(4, lineH / 2);
    const aboveY = plot.y - Math.max(4, lineH / 2) - lineH;
    const centered = (s: string, at: number, y: number) => {
      const w = rc.measureWidth(s, font);
      const x = Math.max(bounds.x + 2, Math.min(at - w / 2, bounds.x + bounds.width - 2 - w));
      rc.drawText(s, x, y, font, subtle, { baseline: "top" });
    };
    const leftOfPlot = (s: string, at: number, maxWidth: number) => {
      const text = rc.ellipsize(s, font, maxWidth);
      const w = rc.measureWidth(text, font);
      rc.drawText(text, plot.x - GAP - w, at, font, subtle, { baseline: "middle" });
    };
    const rightOfPlot = (s: string, at: number) => {
      rc.drawText(s, plot.x + plot.width + GAP, at, font, subtle, { baseline: "middle" });
    };

    // The value labels: down the left of a standing chart, along the bottom of one on its side -
    // and the secondary axis' down the right, or along the top.
    const { scale: primary, v } = f.primary;
    primary.ticks.forEach((t, i) => {
      const at = v(t);
      if (!Number.isFinite(at)) return;
      if (horizontal) centered(primary.labels[i]!, at, belowY);
      else leftOfPlot(primary.labels[i]!, at, plot.x - bounds.x);
    });
    if (f.secondary) {
      const { scale: second, v: v2 } = f.secondary;
      second.ticks.forEach((t, i) => {
        const at = v2(t);
        if (!Number.isFinite(at)) return;
        if (horizontal) centered(second.labels[i]!, at, aboveY);
        else rightOfPlot(second.labels[i]!, at);
      });
    }

    // The category labels, thinned until they stop running into each other, and only those in view.
    const cats = this.showXAxis ? this.#categoryTicks(f.x, f.count) : [];
    if (cats.length === 0) return;
    const positions = cats.map((c) => (c.at === null ? f.u(0, c.index) : f.catStart + c.at * f.catLen));
    let spacing = Infinity;
    for (let i = 1; i < positions.length; i++) spacing = Math.min(spacing, Math.abs(positions[i]! - positions[i - 1]!));
    if (!Number.isFinite(spacing)) spacing = f.catLen;
    const size = horizontal ? lineH + 2 : Math.max(0, ...cats.map((c) => rc.measureWidth(c.label, font))) + 8;
    const stride = labelStride(size, spacing);
    for (let i = 0; i < cats.length; i += stride) {
      const p = positions[i]!;
      if (!Number.isFinite(p) || p < f.catStart - 0.5 || p > f.catStart + f.catLen + 0.5) continue;
      if (horizontal) leftOfPlot(cats[i]!.label, p, plot.x - bounds.x - GAP);
      else centered(cats[i]!.label, p, belowY);
    }
  }

  /** How thick the bars of one category may be, across all of them. */
  #barGroup(f: Frame, bars: number): number {
    if (!f.x) {
      const slot = f.catLen / Math.max(1, f.count) / (this.#view.to - this.#view.from);
      return slot * (this.#stacked ? 0.6 : 0.72);
    }
    // On a numeric axis the bars are as thick as the tightest pair of neighbors allows.
    const slots = [...new Set(Array.from({ length: f.count }, (_, i) => f.u(0, i)).filter(Number.isFinite))].sort((a, b) => a - b);
    let gap = Infinity;
    for (let i = 1; i < slots.length; i++) gap = Math.min(gap, slots[i]! - slots[i - 1]!);
    const room = Number.isFinite(gap) ? gap : f.catLen * 0.1;
    return Math.max(2 * bars, Math.min(room * 0.72, 48 * (this.#stacked ? 1 : bars)));
  }

  #paintBars(rc: RenderContext, theme: Theme, f: Frame, stack: Stack): void {
    const bars = this.#series.map((s, i) => ({ s, i })).filter(({ s }) => this.kindOf(s) === "bar" || this.kindOf(s) === "range");
    if (bars.length === 0 || f.count === 0) return;
    const columns = this.#stacked ? 1 : bars.length;
    const group = this.#barGroup(f, columns);
    const thick = group / columns;
    // A segment in the middle of a stack has no corner of its own to round.
    const radius = this.#stacked ? 0 : Math.min(3, theme.metrics.controlCornerRadius, thick / 4);
    const lo = f.catStart - group;
    const hi = f.catStart + f.catLen + group;
    bars.forEach(({ s, i }, b) => {
      const color = this.#colorOf(theme, i);
      const axis = this.#axisOf(f, i);
      const column = this.#stacked ? 0 : b;
      for (let c = 0; c < s.values.length; c++) {
        if (!isValue(s.values[c])) continue;
        const mid = f.u(i, c);
        if (!Number.isFinite(mid) || mid < lo || mid > hi) continue;
        const ranged = this.kindOf(s) === "range";
        const low = s.lower?.[c];
        if (ranged && !isValue(low)) continue;
        const v1 = axis.v(ranged ? s.values[c]! : stack.to[i]![c]!);
        let v0 = ranged ? axis.v(low!) : this.#stacked ? axis.v(stack.from[i]![c]!) : axis.base;
        if (!Number.isFinite(v0)) v0 = axis.base;
        if (!Number.isFinite(v1)) continue;
        const length = Math.abs(v0 - v1);
        if (length <= 0) continue;
        const u0 = mid - group / 2 + column * thick + 0.5;
        const w = Math.max(1, thick - 1);
        const bar: Rect = f.horizontal
          ? { x: Math.min(v0, v1), y: u0, width: length, height: w }
          : { x: u0, y: Math.min(v0, v1), width: w, height: length };
        rc.fillRect(bar, c === this.#hovered && this.#hoveredSeries < 0 ? withAlpha(color, color.a * 0.8) : color, radius);
      }
    });
  }

  #paintMarks(rc: RenderContext, theme: Theme, f: Frame, stack: Stack): void {
    const toScreen = (p: Point) => f.pt(p.x, p.y);
    const mapTriangles = (tris: Triangle[]) =>
      f.horizontal ? tris.map((t) => ({ a: toScreen(t.a), b: toScreen(t.b), c: toScreen(t.c), edges: t.edges })) : tris;
    // An area is cut into strips whose shared sides run straight across the category axis, and the
    // fill decides each side of a shared edge separately. Were one to fall exactly on a pixel center,
    // rounding could leave that column to neither strip - a hairline seam through the fill - so the
    // seams are put on device pixel boundaries, where no pixel center can be.
    const t = rc.transform;
    const dpr = rc.gpu?.size?.dpr ?? 1;
    const offset = f.horizontal ? t.ty : t.tx;
    const snapU = (u: number) => (Math.round((u * t.scale + offset) * dpr) / dpr - offset) / t.scale;
    const inView = (u: number) => u >= f.catStart - PAD && u <= f.catStart + f.catLen + PAD;

    this.#series.forEach((s, i) => {
      const kind = this.kindOf(s);
      if (takesSlot(kind) || kind === "band" || kind === "baseline" || kind === "kagi") return;
      const color = this.#colorOf(theme, i);
      const stacked = this.stacks(s);
      const axis = this.#axisOf(f, i);

      // Worked out in (u, v) - across, along - and only turned to screen x and y to draw.
      const placed = (c: number) => {
        const val = s.values[c];
        if (!isValue(val)) return null;
        const p = { x: f.u(i, c), y: axis.v(stacked ? stack.to[i]![c]! : val) };
        return Number.isFinite(p.x) && Number.isFinite(p.y) ? p : null;
      };
      // Points in view, and the neighbor either side of them so a line still runs off the edge.
      // On a long live series zoomed in, that is most of the work saved.
      const runs = placedRuns(s.values.length, placed).flatMap((run) =>
        kind === "points" ? [run.filter((r) => inView(r.p.x))] : clipRun(run, inView),
      );
      for (const run of runs) {
        if (run.length === 0) continue;
        const tops = run.map((r) => r.p);
        if (kind === "points") {
          for (const p of tops) rc.fillCircle(toScreen(p), this.lineWidth + 2, color);
          continue;
        }
        const stepped = kind === "step" || s.curve === "step";
        if (kind === "area") {
          const acrossAxis = (p: Point) => ({ x: snapU(p.x), y: p.y });
          const bottoms = run.map((r) => {
            const from = stacked ? axis.v(stack.from[i]![r.c]!) : axis.base;
            return { x: r.p.x, y: Number.isFinite(from) ? from : axis.base };
          });
          const top = stepped ? stepPoints(tops) : tops;
          const bottom = stepped ? stepPoints(bottoms) : bottoms;
          rc.fillTriangles(mapTriangles(bandTriangles(top.map(acrossAxis), bottom.map(acrossAxis))), withAlpha(color, color.a * (stacked ? 0.45 : 0.22)));
        }
        if (tops.length === 1) rc.fillCircle(toScreen(tops[0]!), Math.max(2, this.lineWidth), color);
        else rc.fillTriangles(strokeTriangles((stepped ? stepPoints(tops) : tops).map(toScreen), false, this.lineWidth), color);
        if (this.showMarkers) {
          for (const p of tops) if (inView(p.x)) rc.fillCircle(toScreen(p), this.lineWidth + 1.5, color);
        }
      }
    });
  }

  /**
   * Bands: the fill between a series' `lower` values and its `values`, edged top and bottom with a
   * thin line. Drawn first, under everything else, since a band is the ground a price moves across.
   */
  #paintBands(rc: RenderContext, theme: Theme, f: Frame): void {
    const toScreen = (p: Point) => f.pt(p.x, p.y);
    const inView = (u: number) => u >= f.catStart - PAD && u <= f.catStart + f.catLen + PAD;
    const edge = Math.max(1, this.lineWidth / 2);
    this.#series.forEach((s, i) => {
      if (this.kindOf(s) !== "band" || !s.lower) return;
      const color = this.#colorOf(theme, i);
      const axis = this.#axisOf(f, i);
      const lower = s.lower;
      const placed = (c: number) => {
        const top = s.values[c];
        const bottom = lower[c];
        if (!isValue(top) || !isValue(bottom)) return null;
        const p = { x: f.u(i, c), y: axis.v(top) };
        return Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(axis.v(bottom)) ? p : null;
      };
      for (const run of placedRuns(s.values.length, placed).flatMap((r) => clipRun(r, inView))) {
        if (run.length < 2) continue;
        const tops = run.map((r) => r.p);
        const bottoms = run.map((r) => ({ x: r.p.x, y: axis.v(lower[r.c]!) }));
        const tris = bandTriangles(tops, bottoms);
        rc.fillTriangles(
          f.horizontal ? tris.map((t) => ({ a: toScreen(t.a), b: toScreen(t.b), c: toScreen(t.c), edges: t.edges })) : tris,
          withAlpha(color, color.a * 0.16),
        );
        rc.fillTriangles(strokeTriangles(tops.map(toScreen), false, edge), withAlpha(color, color.a * 0.7));
        rc.fillTriangles(strokeTriangles(bottoms.map(toScreen), false, edge), withAlpha(color, color.a * 0.7));
      }
    });
  }

  /**
   * The crosshair: a line across the plot at the pointer, with the value there read out on the
   * value axis (and the secondary, when there is one), and the category or date on the other axis.
   * Along the category axis it sits on the hovered category - the guide line - and only follows
   * the pointer freely where there is nothing to snap to. Arrowing through with the keyboard, with
   * no pointer, still reads the date out.
   */
  #paintCrosshair(rc: RenderContext, theme: Theme, font: FontSpec, f: Frame, bounds: Rect): void {
    const { plot, horizontal } = f;
    const p = this.#pointer;
    const inside = p !== null && p.x >= plot.x && p.x <= plot.x + plot.width && p.y >= plot.y && p.y <= plot.y + plot.height;
    const line = withAlpha(theme.palette.textSubtle, 0.7);
    const lineH = rc.lineHeight(font);
    const readout = (text: string, at: Point, align: "left" | "right" | "center", vertical: "top" | "bottom" | "middle") => {
      const w = Math.ceil(rc.measureWidth(text, font) + 8);
      const h = Math.ceil(lineH + 2);
      let x = align === "left" ? at.x : align === "right" ? at.x - w : at.x - w / 2;
      let y = vertical === "top" ? at.y : vertical === "bottom" ? at.y - h : at.y - h / 2;
      x = Math.max(bounds.x, Math.min(x, bounds.x + bounds.width - w));
      y = Math.max(bounds.y, Math.min(y, bounds.y + bounds.height - h));
      const box = { x, y, width: w, height: h };
      theme.drawChartReadout(rc, box);
      rc.drawTextCentered(text, box, font, theme.chartReadoutTextColor());
    };

    if (inside) {
      // Across the values, at the pointer.
      if (horizontal) rc.vLine(Math.round(p.x), plot.y, plot.height, line);
      else rc.hLine(plot.x, Math.round(p.y), plot.width, line);
      const fraction = horizontal ? (p.x - plot.x) / plot.width : (plot.y + plot.height - p.y) / plot.height;
      const primary = this.formatValue(scaleValue(fraction, f.primary.scale));
      if (horizontal) readout(primary, { x: p.x, y: plot.y + plot.height + 2 }, "center", "top");
      else readout(primary, { x: plot.x - 2, y: p.y }, "right", "middle");
      if (f.secondary) {
        const second = this.formatValue(scaleValue(fraction, f.secondary.scale), true);
        if (horizontal) readout(second, { x: p.x, y: plot.y - 2 }, "center", "bottom");
        else readout(second, { x: plot.x + plot.width + 2, y: p.y }, "left", "middle");
      }
    }

    // Along the categories: on the hovered one, or at the pointer when nothing is hovered.
    let u: number | null = null;
    let label: string | null = null;
    const c = this.#hovered;
    if (c >= 0 && this.#hoveredSeries < 0) {
      u = f.u(0, c);
      label = this.categoryLabel(c);
    } else if (c >= 0) {
      u = f.u(this.#hoveredSeries, c);
      const x = this.xOf(this.#hoveredSeries, c);
      label = x === null ? null : this.formatX(x);
    } else if (inside) {
      u = horizontal ? p.y : p.x;
      if (f.x) label = this.formatX(scaleValue((u - f.catStart) / f.catLen, f.x));
      if (horizontal) rc.hLine(plot.x, Math.round(u), plot.width, line);
      else rc.vLine(Math.round(u), plot.y, plot.height, line);
    }
    if (u === null || label === null || !Number.isFinite(u) || u < f.catStart - 0.5 || u > f.catStart + f.catLen + 0.5) return;
    if (horizontal) readout(label, { x: plot.x - 2, y: u }, "right", "middle");
    else readout(label, { x: u, y: plot.y + plot.height + 2 }, "center", "top");
  }

  /** A rectangle given across (u) and along (v), turned the right way up for the screen. */
  #box(f: Frame, u0: number, u1: number, v0: number, v1: number): Rect {
    const a = Math.min(u0, u1);
    const b = Math.min(v0, v1);
    const across = Math.abs(u1 - u0);
    const along = Math.max(1, Math.abs(v1 - v0));
    return f.horizontal ? { x: b, y: a, width: along, height: across } : { x: a, y: b, width: across, height: along };
  }

  /** The value frame an annotation or level is measured against. */
  #annotationAxis(f: Frame, axis: ChartValueAxis | undefined): ValueFrame {
    return axis === "secondary" && f.secondary ? f.secondary : f.primary;
  }

  #annotationColor(theme: Theme, color: Color | null | undefined): Color {
    return this.#dim(color ?? theme.palette.text);
  }

  /**
   * Where `at` falls along the category axis: through the x axis on a numeric or time axis; between
   * the two periods either side of it on a time axis with `skipGaps`, so a date in a weekend sits
   * between Friday and Monday; and at a (possibly fractional) index on a category axis.
   */
  #uAt(f: Frame, at: ChartAt): number {
    const t = at instanceof Date ? at.getTime() : at;
    if (!Number.isFinite(t)) return Number.NaN;
    if (f.x) return f.catStart + scaleFraction(t, f.x) * f.catLen;
    const between = (i: number, frac: number) => f.u(0, i) + frac * (f.u(0, i + 1) - f.u(0, i));
    if (this.#xScale === "time") {
      const last = f.count - 1;
      if (last < 0) return Number.NaN;
      const first = this.xOf(0, 0);
      const end = this.xOf(0, last);
      if (first === null || end === null) return Number.NaN;
      if (t <= first) return f.u(0, 0);
      if (t >= end) return f.u(0, last);
      let lo = 0;
      let hi = last;
      while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        const x = this.xOf(0, mid);
        if (x !== null && x <= t) lo = mid;
        else hi = mid;
      }
      const a = this.xOf(0, lo)!;
      const b = this.xOf(0, hi)!;
      return between(lo, b > a ? (t - a) / (b - a) : 0);
    }
    const i = Math.floor(t);
    return between(i, t - i);
  }

  /** A straight rule from `a` to `b`, given in (u, v), solid or dashed. */
  #rule(rc: RenderContext, f: Frame, a: Point, b: Point, color: Color, thickness: number, dashed: boolean): void {
    const p = f.pt(a.x, a.y);
    const q = f.pt(b.x, b.y);
    if (!dashed) {
      rc.drawLine(p, q, thickness, color);
      return;
    }
    const length = Math.hypot(q.x - p.x, q.y - p.y);
    if (!(length > 0)) return;
    const dx = (q.x - p.x) / length;
    const dy = (q.y - p.y) / length;
    for (let d = 0; d < length; d += 9) {
      const e = Math.min(length, d + 5);
      rc.drawLine({ x: p.x + dx * d, y: p.y + dy * d }, { x: p.x + dx * e, y: p.y + dy * e }, thickness, color);
    }
  }

  /** Shaded stretches: `xrange` across the categories and `yrange` along the values, under everything. */
  #paintRanges(rc: RenderContext, theme: Theme, font: FontSpec, f: Frame): void {
    const vLo = f.horizontal ? f.plot.x : f.plot.y + f.plot.height;
    const vHi = f.horizontal ? f.plot.x + f.plot.width : f.plot.y;
    const uLo = f.catStart;
    const uHi = f.catStart + f.catLen;
    for (const a of this.#annotations) {
      if (a.type !== "xrange" && a.type !== "yrange") continue;
      const color = this.#annotationColor(theme, a.color);
      let r: Rect;
      if (a.type === "xrange") {
        const u0 = this.#uAt(f, a.from);
        const u1 = this.#uAt(f, a.to);
        if (!Number.isFinite(u0) || !Number.isFinite(u1)) continue;
        r = this.#box(f, u0, u1, vLo, vHi);
      } else {
        const axis = this.#annotationAxis(f, a.axis);
        const v0 = axis.v(a.from);
        const v1 = axis.v(a.to);
        if (!Number.isFinite(v0) || !Number.isFinite(v1)) continue;
        r = this.#box(f, uLo, uHi, v0, v1);
      }
      rc.fillRect(r, withAlpha(color, color.a * 0.12));
      if (a.label) rc.drawText(a.label, r.x + 4, r.y + 3, font, withAlpha(color, color.a * 0.8), { baseline: "top", maxWidth: Math.max(0, r.width - 8) });
    }
  }

  /**
   * The volume profile: how much traded at each price, as bars from one end of the category axis,
   * the busiest price - the point of control - a shade deeper. Worked out across (u) and along (v)
   * like everything else, so on a chart lying on its side the prices run across the page and the
   * bars grow up from the bottom.
   */
  #paintVolumeProfile(rc: RenderContext, theme: Theme, f: Frame): void {
    const vp = this.volumeProfile;
    if (!vp) return;
    const pricesIndex = this.#series.findIndex((s) => s.name === vp.prices);
    const prices = this.#series[pricesIndex];
    const volume = typeof vp.volume === "string" ? this.#series.find((s) => s.name === vp.volume)?.values : vp.volume;
    if (!prices?.ohlc || !volume) return;
    const bins = vp.bins ?? 24;
    const source = typeof vp.volume === "string" ? vp.volume : "values";
    const profile = this.#memo("profile", `${vp.prices}|${source}|${volume.length}|${bins}`, () => volumeProfile(prices.ohlc!, volume, bins));
    if (!profile) return;
    const axis = this.#axisOf(f, pricesIndex);
    const most = Math.max(...profile.volumes);
    if (!(most > 0)) return;
    const reach = f.catLen * Math.min(1, Math.max(0, vp.width ?? 0.25));
    const base = this.#dim(vp.color ?? theme.palette.textSubtle);
    const near = f.catStart;
    const far = f.catStart + f.catLen;
    profile.volumes.forEach((v, b) => {
      if (v <= 0) return;
      const v0 = axis.v(profile.low + b * profile.step);
      const v1 = axis.v(profile.low + (b + 1) * profile.step);
      if (!Number.isFinite(v0) || !Number.isFinite(v1)) return;
      const length = (v / most) * reach;
      // A pixel's gap between bins, so neighbors read as separate bars.
      const lo = Math.min(v0, v1) + 0.5;
      const hi = Math.max(lo + 1, Math.max(v0, v1) - 0.5);
      const bar = vp.side === "left" ? this.#box(f, near, near + length, lo, hi) : this.#box(f, far - length, far, lo, hi);
      rc.fillRect(bar, withAlpha(base, base.a * (v === most ? 0.45 : 0.22)));
    });
  }

  /**
   * Waterfalls: each change floating from the running total, green up and red down, the totals in
   * the series' own color from zero, and a hairline from the end of one bar to the start of the
   * next so the eye can follow the sum.
   */
  #paintWaterfalls(rc: RenderContext, theme: Theme, f: Frame): void {
    const subtle = withAlpha(theme.palette.textSubtle, 0.6);
    this.#series.forEach((s, i) => {
      if (this.kindOf(s) !== "waterfall") return;
      const axis = this.#axisOf(f, i);
      const steps = this.waterfallOf(s);
      const width = this.#barGroup(f, 1) * 0.85;
      const half = width / 2;
      const own = this.#colorOf(theme, i);
      steps.forEach((step, c) => {
        if (!step) return;
        const mid = f.u(i, c);
        const v0 = axis.v(step.from);
        const v1 = axis.v(step.to);
        if (![mid, v0, v1].every(Number.isFinite)) return;
        const color = step.total ? own : this.#trend(theme, step.to >= step.from);
        rc.fillRect(this.#box(f, mid - half, mid + half, v0, v1), color, Math.min(2, theme.metrics.controlCornerRadius));
        const next = steps[c + 1];
        const nextMid = f.u(i, c + 1);
        if (next && Number.isFinite(nextMid)) rc.fillRect(this.#box(f, mid + half, nextMid - half, v1 - 0.5, v1 + 0.5), subtle);
      });
    });
  }

  /**
   * Baselines: a line and the fill between it and `baseValue`, green where it is above and red
   * where it is below, cut cleanly where it crosses.
   */
  #paintBaselines(rc: RenderContext, theme: Theme, f: Frame): void {
    const toScreen = (p: Point) => f.pt(p.x, p.y);
    const inView = (u: number) => u >= f.catStart - PAD && u <= f.catStart + f.catLen + PAD;
    const up = this.#trend(theme, true);
    const down = this.#trend(theme, false);
    this.#series.forEach((s, i) => {
      if (this.kindOf(s) !== "baseline") return;
      const axis = this.#axisOf(f, i);
      const level = this.baseValueOf(s);
      const base = axis.v(level);
      if (!Number.isFinite(base)) return;
      // Which way "above" runs on screen: up a standing chart, right on one lying down.
      const dir = Math.sign(axis.v(level + Math.abs(level || 1)) - base) || -1;
      const above = (v: number) => (v - base) * dir > 0;
      this.#rule(rc, f, { x: f.catStart, y: base }, { x: f.catStart + f.catLen, y: base }, withAlpha(theme.palette.textSubtle, 0.7), 1, true);
      const placed = (c: number) => {
        const v = s.values[c];
        if (!isValue(v)) return null;
        const p = { x: f.u(i, c), y: axis.v(v) };
        return Number.isFinite(p.x) && Number.isFinite(p.y) ? p : null;
      };
      for (const run of placedRuns(s.values.length, placed).flatMap((r) => clipRun(r, inView))) {
        const tops = run.map((r) => r.p);
        if (tops.length < 2) continue;
        const tris = bandTriangles(tops, tops.map((p) => ({ x: p.x, y: base })));
        const upper: Triangle[] = [];
        const lower: Triangle[] = [];
        for (const t of tris) (above((t.a.y + t.b.y + t.c.y) / 3) ? upper : lower).push(t);
        const screen = (ts: Triangle[]) => (f.horizontal ? ts.map((t) => ({ a: toScreen(t.a), b: toScreen(t.b), c: toScreen(t.c), edges: t.edges })) : ts);
        rc.fillTriangles(screen(upper), withAlpha(up, up.a * 0.22));
        rc.fillTriangles(screen(lower), withAlpha(down, down.a * 0.22));
        for (const piece of splitAtLevel(tops, base)) {
          if (piece.points.length < 2) continue;
          const color = above(piece.points.reduce((sum, p) => sum + p.y, 0) / piece.points.length) ? up : down;
          rc.fillTriangles(strokeTriangles(piece.points.map(toScreen), false, this.lineWidth), color);
        }
      }
    });
  }

  /**
   * Kagi: up at each turning point's column and across to the next, the line thick and green (yang)
   * once it has climbed past the last shoulder, and thin and red (yin) once it has fallen through
   * the last waist - changing partway up or down a stroke, where it crosses.
   */
  #paintKagi(rc: RenderContext, theme: Theme, f: Frame): void {
    const up = this.#trend(theme, true);
    const down = this.#trend(theme, false);
    const thick = Math.max(2, this.lineWidth * 1.6);
    const thin = Math.max(1, this.lineWidth * 0.6);
    this.#series.forEach((s, i) => {
      if (this.kindOf(s) !== "kagi") return;
      const axis = this.#axisOf(f, i);
      const points: { u: number; value: number }[] = [];
      s.values.forEach((v, c) => {
        if (isValue(v)) points.push({ u: f.u(i, c), value: v });
      });
      if (points.length < 2) return;
      const vertical = (u: number, a: number, b: number, yang: boolean) => {
        const w = yang ? thick : thin;
        rc.fillRect(this.#box(f, u - w / 2, u + w / 2, axis.v(a), axis.v(b)), yang ? up : down);
      };
      const across = (u0: number, u1: number, level: number, yang: boolean) => {
        const w = yang ? thick : thin;
        const v = axis.v(level);
        rc.fillRect(this.#box(f, u0 - w / 2, u1 + w / 2, v - w / 2, v + w / 2), yang ? up : down);
      };
      let yang = points[1]!.value > points[0]!.value;
      let shoulder: number | null = yang ? null : points[0]!.value;
      let waist: number | null = yang ? points[0]!.value : null;
      for (let k = 1; k < points.length; k++) {
        const a = points[k - 1]!.value;
        const b = points[k]!.value;
        const u = points[k]!.u;
        across(points[k - 1]!.u, u, a, yang);
        if (b > a) {
          if (!yang && shoulder !== null && b > shoulder) {
            vertical(u, a, shoulder, false);
            vertical(u, shoulder, b, true);
            yang = true;
          } else vertical(u, a, b, yang);
          shoulder = b;
        } else {
          if (yang && waist !== null && b < waist) {
            vertical(u, a, waist, true);
            vertical(u, waist, b, false);
            yang = false;
          } else vertical(u, a, b, yang);
          waist = b;
        }
      }
    });
  }

  /** Point-and-figure: a column of Xs for each rise and of Os for each fall, one mark a box. */
  #paintPointFigure(rc: RenderContext, s: OwnSeries, i: number, f: Frame, axis: ValueFrame, width: number, up: Color, down: Color): void {
    const box = this.boxSizeOf(s);
    const stroke = Math.max(1, this.lineWidth * 0.75);
    const lo = f.catStart - width;
    const hi = f.catStart + f.catLen + width;
    s.ohlc!.forEach((o, c) => {
      if (!isOhlc(o)) return;
      const mid = f.u(i, c);
      if (!Number.isFinite(mid) || mid < lo || mid > hi) return;
      const rising = o.close >= o.open;
      const from = Math.min(o.open, o.close);
      const count = Math.round(Math.abs(o.close - o.open) / box) + 1;
      for (let k = 0; k < count; k++) {
        const level = from + k * box;
        const v0 = axis.v(level);
        const v1 = axis.v(level + box);
        if (!Number.isFinite(v0) || !Number.isFinite(v1)) continue;
        const half = (Math.min(width, Math.abs(v1 - v0)) * 0.8) / 2;
        const center = f.pt(mid, (v0 + v1) / 2);
        if (rising) {
          rc.drawLine({ x: center.x - half, y: center.y - half }, { x: center.x + half, y: center.y + half }, stroke, up);
          rc.drawLine({ x: center.x - half, y: center.y + half }, { x: center.x + half, y: center.y - half }, stroke, up);
        } else {
          rc.strokeCircle(center, half, stroke, down);
        }
      }
    });
  }

  /** Price lines, trend lines and event markers: drawn over the series, inside the plot. */
  #paintAnnotationLines(rc: RenderContext, theme: Theme, font: FontSpec, f: Frame): void {
    const uLo = f.catStart;
    const uHi = f.catStart + f.catLen;
    for (const a of this.#annotations) {
      const color = this.#annotationColor(theme, a.color);
      if (a.type === "line") {
        const v = this.#annotationAxis(f, a.axis).v(a.value);
        if (Number.isFinite(v)) this.#rule(rc, f, { x: uLo, y: v }, { x: uHi, y: v }, color, 1, a.dashed ?? false);
        continue;
      }
      if (a.type === "trend") {
        const axis = this.#annotationAxis(f, a.axis);
        const p = { x: this.#uAt(f, a.from.at), y: axis.v(a.from.value) };
        let q = { x: this.#uAt(f, a.to.at), y: axis.v(a.to.value) };
        if (![p.x, p.y, q.x, q.y].every(Number.isFinite)) continue;
        if (a.extend && q.x !== p.x) {
          const end = q.x > p.x ? uHi : uLo;
          q = { x: end, y: p.y + ((q.y - p.y) * (end - p.x)) / (q.x - p.x) };
        }
        this.#rule(rc, f, p, q, color, Math.max(1, this.lineWidth * 0.75), false);
        if (a.label) {
          const at = f.pt(q.x, q.y);
          rc.drawText(a.label, at.x + 4, at.y - 2, font, color, { baseline: "bottom" });
        }
        continue;
      }
      if (a.type === "marker") {
        const u = this.#uAt(f, a.at);
        if (!Number.isFinite(u)) continue;
        const axis = this.#annotationAxis(f, a.axis);
        const shape = a.shape ?? "dot";
        // Without a value, a marker sits just inside the top of the plot.
        const top = axis.v(axis.scale.max);
        const inward = Math.sign(axis.v(axis.scale.min) - top) || 1;
        const v = a.value !== undefined ? axis.v(a.value) : top + inward * 10;
        if (!Number.isFinite(v)) continue;
        const size = 10;
        // An up arrow sits below its point and a down arrow above it, pointing at it.
        const offset = shape === "up" ? 1 : shape === "down" ? -1 : 0;
        const at = f.pt(u, v + offset * inward * (size / 2 + 3));
        const r = { x: at.x - size / 2, y: at.y - size / 2, width: size, height: size };
        if (shape === "dot") rc.fillCircle(at, 4, color);
        else if (shape === "flag") {
          const pole = f.pt(u, v);
          rc.vLine(Math.round(pole.x), pole.y - 14, 14, color);
          rc.fillRect({ x: Math.round(pole.x), y: pole.y - 14, width: 9, height: 6 }, color);
        } else {
          const direction = f.horizontal ? (shape === "up" ? "right" : "left") : shape;
          rc.fillTriangle(r, direction, color);
        }
        if (a.label) {
          const below = shape === "up";
          rc.drawTextCentered(a.label, { x: at.x - 60, y: below ? at.y + size / 2 + 1 : at.y - size / 2 - 17, width: 120, height: 16 }, font, color);
        }
      }
    }
  }

  /**
   * The levels read out on the value axes: each `line` annotation's value (and label), and with
   * `lastValueMarkers` each series' latest value with a dashed line across at it, in the series'
   * color - a candle series' in the color of its last candle.
   */
  #paintLevelReadouts(rc: RenderContext, theme: Theme, font: FontSpec, f: Frame, bounds: Rect): void {
    const lineH = rc.lineHeight(font);
    const pending: { box: Rect; fill: Color; text: string; secondary: boolean }[] = [];
    const readout = (text: string, fill: Color, v: number, secondary: boolean) => {
      if (!Number.isFinite(v)) return;
      const w = Math.ceil(rc.measureWidth(text, font) + 8);
      const h = Math.ceil(lineH + 2);
      let box: Rect;
      if (f.horizontal) {
        const y = secondary ? f.plot.y - 2 - h : f.plot.y + f.plot.height + 2;
        box = { x: v - w / 2, y, width: w, height: h };
      } else {
        const x = secondary ? f.plot.x + f.plot.width + 2 : f.plot.x - 2 - w;
        box = { x, y: v - h / 2, width: w, height: h };
      }
      box.x = Math.max(bounds.x, Math.min(box.x, bounds.x + bounds.width - w));
      box.y = Math.max(bounds.y, Math.min(box.y, bounds.y + bounds.height - h));
      pending.push({ box, fill, text, secondary });
    };
    // Readouts on one side are spread apart along the axis until none covers another - a target
    // just above the latest price would otherwise hide it - then drawn.
    const flush = () => {
      const along = f.horizontal ? "x" : "y";
      const size = f.horizontal ? "width" : "height";
      for (const secondary of [false, true]) {
        const side = pending.filter((r) => r.secondary === secondary).sort((a, b) => a.box[along] - b.box[along]);
        for (let k = 1; k < side.length; k++) {
          const prev = side[k - 1]!.box;
          const box = side[k]!.box;
          const overlap = prev[along] + prev[size] + 1 - box[along];
          if (overlap > 0) box[along] += overlap;
        }
      }
      for (const r of pending) {
        rc.fillRect(r.box, r.fill, Math.min(3, theme.metrics.controlCornerRadius));
        const ink: Color = luminance(r.fill) > 0.55 ? { r: 0, g: 0, b: 0, a: 1 } : { r: 1, g: 1, b: 1, a: 1 };
        rc.drawTextCentered(r.text, r.box, font, ink);
      }
    };
    for (const a of this.#annotations) {
      if (a.type !== "line") continue;
      const secondary = a.axis === "secondary" && f.secondary !== null;
      const v = this.#annotationAxis(f, a.axis).v(a.value);
      const value = this.formatValue(a.value, secondary);
      readout(a.label ? `${a.label} ${value}` : value, this.#annotationColor(theme, a.color), v, secondary);
    }
    if (!this.lastValueMarkers) {
      flush();
      return;
    }
    this.#series.forEach((s, i) => {
      const kind = this.kindOf(s);
      if (kind === "band" || kind === "range" || s.x) return;
      let last = -1;
      for (let c = s.values.length - 1; c >= 0; c--) {
        if (isValue(s.values[c])) {
          last = c;
          break;
        }
      }
      if (last < 0) return;
      let value = s.values[last]!;
      let color = this.#colorOf(theme, i);
      if (kind === "waterfall") value = this.waterfallOf(s)[last]?.to ?? value;
      else if (kind === "baseline") color = this.#trend(theme, value >= this.baseValueOf(s));
      else if (isFinancialKind(kind)) {
        const o = s.ohlc?.[last];
        if (isOhlc(o)) color = this.#trend(theme, o.close >= o.open);
      }
      const axis = this.#axisOf(f, i);
      const v = axis.v(value);
      if (!Number.isFinite(v)) return;
      this.#rule(rc, f, { x: f.catStart, y: v }, { x: f.catStart + f.catLen, y: v }, withAlpha(color, color.a * 0.6), 1, true);
      readout(this.formatValue(value, this.#isSecondary(s)), color, v, this.#isSecondary(s) && f.secondary !== null);
    });
    flush();
  }


  #trend(theme: Theme, rising: boolean): Color {
    return this.#dim((rising ? this.upColor : this.downColor) ?? theme.chartTrendColor(rising));
  }

  /**
   * Candles and OHLC bars: a wick from low to high and a body from open to close, or a stem with
   * the open ticked to the left and the close to the right. Green when the period closed at or
   * above where it opened, red when below - the colors come from the theme or `upColor` and
   * `downColor`. Worked out in (u, v) like everything else, so a chart on its side gets them on
   * their sides too.
   */
  #paintCandles(rc: RenderContext, theme: Theme, f: Frame): void {
    const series = this.#series.map((s, i) => ({ s, i })).filter(({ s }) => s.ohlc && isFinancialKind(this.kindOf(s)));
    if (series.length === 0 || f.count === 0) return;
    const width = Math.max(3, this.#barGroup(f, 1) * 0.8);
    const lo = f.catStart - width;
    const hi = f.catStart + f.catLen + width;
    // A rectangle given across (u) and along (v), turned the right way up for the screen.
    const box = (u0: number, u1: number, v0: number, v1: number): Rect => {
      const a = Math.min(u0, u1);
      const b = Math.min(v0, v1);
      const across = Math.abs(u1 - u0);
      const along = Math.max(1, Math.abs(v1 - v0));
      return f.horizontal ? { x: b, y: a, width: along, height: across } : { x: a, y: b, width: across, height: along };
    };
    const up = this.#trend(theme, true);
    const down = this.#trend(theme, false);
    const stem = Math.max(1, Math.round(this.lineWidth / 2));

    for (const { s, i } of series) {
      const axis = this.#axisOf(f, i);
      const kind = this.kindOf(s);
      const candles = kind === "candlestick";
      if (kind === "pointfigure") {
        this.#paintPointFigure(rc, s, i, f, axis, width, up, down);
        continue;
      }
      let previous: Ohlc | null = null;
      s.ohlc!.forEach((o, c) => {
        if (!isOhlc(o)) {
          previous = null;
          return;
        }
        const before: Ohlc | null = previous;
        previous = o;
        const mid = f.u(i, c);
        if (!Number.isFinite(mid) || mid < lo || mid > hi) return;
        const [open, high, low, close] = [axis.v(o.open), axis.v(o.high), axis.v(o.low), axis.v(o.close)];
        if (![open, high, low, close].every(Number.isFinite)) return;
        const rising = this.candleColoring === "previousClose" && before ? o.close >= before.close : o.close >= o.open;
        const color = rising ? up : down;
        const half = width / 2;
        if (kind === "renko") {
          // Bricks sit shoulder to shoulder with no wick: the slot is all brick.
          const brick = (width / 0.8) * 0.96;
          rc.fillRect(box(mid - brick / 2, mid + brick / 2, open, close), color);
          return;
        }
        rc.fillRect(box(mid - stem / 2, mid + stem / 2, low, high), color);
        if (candles) {
          const body = box(mid - half, mid + half, open, close);
          if (o.close >= o.open && this.hollowCandles) {
            rc.fillRect(body, theme.palette.windowBg);
            rc.strokeRect(body, color, stem);
          } else {
            rc.fillRect(body, color);
          }
        } else {
          rc.fillRect(box(mid - half, mid, open - stem / 2, open + stem / 2), color);
          rc.fillRect(box(mid, mid + half, close - stem / 2, close + stem / 2), color);
        }
      });
    }
  }

  /** A financial series' tooltip rows: its open, high, low and close, and how far it moved. */
  #ohlcRows(s: OwnSeries, c: number, prefix: string): TooltipRow[] {
    const o = s.ohlc?.[c];
    const second = this.#isSecondary(s);
    const blank = { r: 0, g: 0, b: 0, a: 0 };
    if (!isOhlc(o)) return [{ text: `${prefix || `${s.name} `}no data`, color: blank }];
    const move = o.close - o.open;
    const pct = o.open !== 0 ? ` (${move >= 0 ? "+" : ""}${((move / o.open) * 100).toFixed(2)}%)` : "";
    return [
      { text: `${prefix}Open: ${this.formatValue(o.open, second)}`, color: blank },
      { text: `${prefix}High: ${this.formatValue(o.high, second)}`, color: blank },
      { text: `${prefix}Low: ${this.formatValue(o.low, second)}`, color: blank },
      { text: `${prefix}Close: ${this.formatValue(o.close, second)}${pct}`, color: blank },
    ];
  }

  /** The tooltip's words for whatever is picked out, or null when nothing is. */
  #tooltipContent(colorOf: (series: number) => Color): TooltipContent | null {
    const c = this.#hovered;
    if (c < 0) return null;
    if (this.#chartType === "treemap") {
      const total = (this.#series[0]?.values ?? []).reduce<number>((sum, v) => sum + (isValue(v) && v > 0 ? v : 0), 0);
      const rows = this.#series.map((s, i) => {
        const v = s.values[c];
        const share = i === 0 && isValue(v) && total > 0 ? ` (${Math.round((v / total) * 1000) / 10}%)` : "";
        return { text: `${s.name}: ${isValue(v) ? this.formatValue(v) : "—"}${share}`, color: colorOf(-1) };
      });
      return { heading: this.categoryLabel(c), rows };
    }
    if (this.radial) {
      const rows = this.#series.map((s) => {
        const v = s.values[c];
        const slice = pieSlices(s.values).find((sl) => sl.index === c);
        const share = slice ? ` (${Math.round(slice.fraction * 1000) / 10}%)` : "";
        return { text: `${s.name}: ${isValue(v) ? this.formatValue(v) : "—"}${share}`, color: colorOf(-1) };
      });
      return { heading: this.categoryLabel(c), rows };
    }
    const si = this.#hoveredSeries;
    if (si >= 0) {
      const s = this.#series[si];
      if (!s) return null;
      const x = this.xOf(si, c);
      const v = s.values[c];
      return {
        heading: s.name,
        rows: [
          { text: `x: ${x === null ? "—" : this.formatX(x)}`, color: colorOf(si) },
          { text: `y: ${isValue(v) ? this.formatValue(v, this.#isSecondary(s)) : "—"}`, color: withAlpha(colorOf(si), 0) },
        ],
      };
    }
    const rows: TooltipRow[] = [];
    const totals = [0, 0];
    const stackedCounts = [0, 0];
    const financial = this.#series.filter((s) => s.ohlc && isFinancialKind(this.kindOf(s))).length;
    this.#series.forEach((s, i) => {
      const v = s.values[c];
      const second = this.#isSecondary(s);
      if (s.ohlc && isFinancialKind(this.kindOf(s))) {
        rows.push(...this.#ohlcRows(s, c, financial > 1 ? `${s.name} ` : ""));
        return;
      }
      if (this.kindOf(s) === "waterfall") {
        const step = this.waterfallOf(s)[c];
        const text = !step ? "—" : step.total ? `total ${this.formatValue(step.to, second)}` : `${step.to >= step.from ? "+" : ""}${this.formatValue(step.to - step.from, second)}`;
        rows.push({ text: `${s.name}: ${text}`, color: colorOf(i) });
        return;
      }
      if (this.kindOf(s) === "band" || this.kindOf(s) === "range") {
        const lo = s.lower?.[c];
        const text = isValue(v) && isValue(lo) ? `${this.formatValue(lo, second)} – ${this.formatValue(v, second)}` : "—";
        rows.push({ text: `${s.name}: ${text}`, color: colorOf(i) });
        return;
      }
      rows.push({ text: `${s.name}: ${isValue(v) ? this.formatValue(v, second) : "—"}`, color: colorOf(i) });
      if (this.stacks(s)) {
        stackedCounts[second ? 1 : 0]!++;
        if (isValue(v)) totals[second ? 1 : 0]! += v;
      }
    });
    if (stackedCounts[0]! > 1) rows.push({ text: `Total: ${this.formatValue(totals[0]!)}`, color: withAlpha(colorOf(0), 0) });
    if (stackedCounts[1]! > 1) rows.push({ text: `Total (right): ${this.formatValue(totals[1]!, true)}`, color: withAlpha(colorOf(0), 0) });
    return { heading: this.categoryLabel(c), rows };
  }

  #paintCartesianHover(rc: RenderContext, theme: Theme, font: FontSpec, f: Frame, stack: Stack, bounds: Rect): void {
    const pal = theme.palette;
    const c = this.#hovered;
    const si = this.#hoveredSeries;
    const content = this.#tooltipContent((i) => this.#colorOf(theme, i));
    if (!content) return;
    const dot = (p: Point, color: Color) => {
      rc.fillCircle(p, this.lineWidth + 3, pal.windowBg);
      rc.fillCircle(p, this.lineWidth + 1.5, color);
    };

    let anchor: Point;
    if (si >= 0) {
      const v = this.#series[si]?.values[c];
      const a = f.u(si, c);
      const b = isValue(v) ? this.#axisOf(f, si).v(v) : Number.NaN;
      if (!Number.isFinite(a) || !Number.isFinite(b) || a < f.catStart - PAD || a > f.catStart + f.catLen + PAD) return;
      anchor = f.pt(a, b);
      const kind = this.kindOf(this.#series[si]!);
      if (!takesSlot(kind) && kind !== "band") dot(anchor, this.#colorOf(theme, si));
    } else {
      const u = f.u(0, c);
      // A category scrolled out of a zoomed view has nothing on screen to point at.
      if (!Number.isFinite(u) || u < f.catStart - 0.5 || u > f.catStart + f.catLen + 0.5) return;
      const guide = withAlpha(pal.textSubtle, 0.5);
      if (f.horizontal) rc.hLine(f.plot.x, Math.round(u), f.plot.width, guide);
      else rc.vLine(Math.round(u), f.plot.y, f.plot.height, guide);
      this.#series.forEach((s, i) => {
        const v = s.values[c];
        const kind = this.kindOf(s);
        if (!isValue(v) || takesSlot(kind) || kind === "band") return;
        const b = this.#axisOf(f, i).v(this.stacks(s) ? stack.to[i]![c]! : v);
        if (Number.isFinite(b)) dot(f.pt(u, b), this.#colorOf(theme, i));
      });
      anchor = f.horizontal ? { x: f.plot.x + f.plot.width * 0.6, y: u } : { x: u, y: f.plot.y };
    }

    if (!this.showTooltip) return;
    this.#paintTooltip(rc, theme, font, content, bounds, (w, h) => {
      if (f.horizontal && si < 0) return { x: anchor.x - w / 2, y: anchor.y + 12 + h <= bounds.y + bounds.height ? anchor.y + 12 : anchor.y - 12 - h };
      const x = anchor.x + 12 + w <= bounds.x + bounds.width ? anchor.x + 12 : anchor.x - 12 - w;
      return { x, y: si >= 0 ? anchor.y - h / 2 : anchor.y };
    });
  }

  // --- treemap ------------------------------------------------------------

  /**
   * The treemap: one tile per category, its area its share of the first series, laid out squarified
   * so the tiles stay near square. With a second series the tiles are colored by it - green for a
   * rise and red for a fall, deeper the further it went - else each in its category's color. A tile
   * with room is labeled with its name, and under it the second series' value or its own.
   */
  #paintTreemap(rc: RenderContext, theme: Theme, font: FontSpec, area: Rect, bounds: Rect): void {
    const first = this.#series[0];
    const key = `${area.x}|${area.y}|${area.width}|${area.height}`;
    const tiles = first ? this.#memo("treemap", key, () => squarify(first.values, area)) : [];
    this.#layout = { type: "treemap", tiles };
    const change = this.#series[1];
    let most = 0;
    for (const v of change?.values ?? []) if (isValue(v)) most = Math.max(most, Math.abs(v));
    const pal = theme.palette;
    const neutral = mixColor(pal.windowBg, pal.textSubtle, 0.35);
    const lineH = rc.lineHeight(font);
    const bold = { ...font, weight: 600 };
    const radius = Math.min(3, theme.metrics.controlCornerRadius);

    tiles.forEach((t, i) => {
      if (!t) return;
      const r = { x: t.x + 1, y: t.y + 1, width: t.width - 2, height: t.height - 2 };
      if (r.width < 1 || r.height < 1) return;
      let fill: Color;
      if (change) {
        const v = change.values[i];
        fill = isValue(v) && most > 0 ? mixColor(neutral, this.#trend(theme, v >= 0), 0.3 + 0.7 * (Math.abs(v) / most)) : neutral;
      } else fill = this.#categoryColor(theme, i);
      fill = this.#dim(fill);
      rc.fillRect(r, fill, radius);
      if (i === this.#hovered) rc.strokeRect(r, pal.focusRing, 2, radius);

      const ink: Color = luminance(fill) > 0.55 ? { r: 0, g: 0, b: 0, a: 0.85 } : { r: 1, g: 1, b: 1, a: 0.95 };
      if (r.width < 36 || r.height < lineH + 6) return;
      rc.drawText(this.categoryLabel(i), r.x + 6, r.y + 4, bold, ink, { baseline: "top", maxWidth: r.width - 12 });
      if (r.height < lineH * 2 + 8) return;
      const shown = change ? change.values[i] : first!.values[i];
      if (!isValue(shown)) return;
      const text = change ? `${shown > 0 ? "+" : ""}${this.formatValue(shown)}` : this.formatValue(shown);
      rc.drawText(text, r.x + 6, r.y + 4 + lineH, font, ink, { baseline: "top", maxWidth: r.width - 12 });
    });

    const hovered = tiles[this.#hovered];
    if (!hovered || !this.showTooltip) return;
    const content = this.#tooltipContent(() => (change ? withAlpha(neutral, 0) : this.#categoryColor(theme, this.#hovered)));
    if (!content) return;
    const center = { x: hovered.x + hovered.width / 2, y: hovered.y + hovered.height / 2 };
    this.#paintTooltip(rc, theme, font, content, bounds, (w, h) => ({
      x: center.x + 12 + w <= bounds.x + bounds.width ? center.x + 12 : center.x - 12 - w,
      y: center.y - h / 2,
    }));
  }


  // --- pie and donut ------------------------------------------------------

  #paintRadial(rc: RenderContext, theme: Theme, font: FontSpec, area: Rect, bounds: Rect): void {
    const radius = Math.min(area.width, area.height) / 2 - POP - 2;
    const center = { x: area.x + area.width / 2, y: area.y + area.height / 2 };
    const n = this.#series.length;
    this.#layout = { type: "radial", center, rings: [] };
    if (radius <= 4 || n === 0) return;

    const hole = this.#chartType === "donut" ? radius * Math.min(0.9, Math.max(0, this.holeRatio)) : 0;
    const band = (radius - hole) / n;
    const rings: Ring[] = this.#series.map((s, i) => ({
      inner: hole + i * band + (i > 0 ? 2 : 0),
      outer: hole + (i + 1) * band,
      slices: pieSlices(s.values),
    }));
    this.#layout = { type: "radial", center, rings };

    const ground = theme.palette.windowBg;
    rings.forEach((ring, i) => {
      const outermost = i === rings.length - 1;
      for (const slice of ring.slices) {
        const pop = outermost && slice.index === this.#hovered ? POP : 0;
        const color = this.#categoryColor(theme, slice.index);
        rc.fillArc(center, ring.inner, ring.outer + pop, slice.start, slice.end, color);
      }
      // Hairlines in the window color part the slices, the way a printed pie is cut.
      if (ring.slices.length > 1) {
        for (const slice of ring.slices) {
          const d = { x: Math.sin(slice.start), y: -Math.cos(slice.start) };
          const reach = ring.outer + (outermost ? POP : 0);
          rc.drawLine(
            { x: center.x + d.x * ring.inner, y: center.y + d.y * ring.inner },
            { x: center.x + d.x * reach, y: center.y + d.y * reach },
            1.5,
            ground,
          );
        }
      }
    });

    if (hole > 28) this.#paintDonutCenter(rc, theme, font, center, hole, rings[0]!);
    if (this.#hovered >= 0) this.#paintRadialHover(rc, theme, font, center, rings, bounds);
  }

  /** The hovered slice's share in the hole, or the first series' total when nothing is hovered. */
  #paintDonutCenter(rc: RenderContext, theme: Theme, font: FontSpec, center: Point, hole: number, ring: Ring): void {
    const pal = theme.palette;
    const lineH = rc.lineHeight(font);
    const slice = ring.slices.find((s) => s.index === this.#hovered);
    const first = this.#series[0]!;
    const caption = slice ? this.categoryLabel(slice.index) : first.name;
    const value = slice
      ? `${Math.round(slice.fraction * 1000) / 10}%`
      : this.formatValue(first.values.reduce<number>((sum, v) => sum + (isValue(v) && v > 0 ? v : 0), 0));
    const w = hole * 1.4;
    const bold = { ...font, weight: 600 };
    const ink = this.effectivelyEnabled ? pal.text : pal.textDisabled;
    const subtle = this.effectivelyEnabled ? pal.textSubtle : pal.textDisabled;
    rc.drawTextCentered(rc.ellipsize(caption, font, w), { x: center.x - w / 2, y: center.y - lineH, width: w, height: lineH }, font, subtle);
    rc.drawTextCentered(rc.ellipsize(value, bold, w), { x: center.x - w / 2, y: center.y, width: w, height: lineH }, bold, ink);
  }

  #paintRadialHover(rc: RenderContext, theme: Theme, font: FontSpec, center: Point, rings: Ring[], bounds: Rect): void {
    const c = this.#hovered;
    const content = this.#tooltipContent(() => this.#categoryColor(theme, c));
    if (!content) return;

    // Anchor on the middle of the hovered slice's outer edge, in the outermost ring that has one.
    let anchor = center;
    for (let i = rings.length - 1; i >= 0; i--) {
      const slice = rings[i]!.slices.find((sl) => sl.index === c);
      if (!slice) continue;
      const mid = (slice.start + slice.end) / 2;
      const reach = rings[i]!.outer + (i === rings.length - 1 ? POP : 0);
      anchor = { x: center.x + Math.sin(mid) * reach, y: center.y - Math.cos(mid) * reach };
      break;
    }
    const right = anchor.x >= center.x;
    if (!this.showTooltip) return;
    this.#paintTooltip(rc, theme, font, content, bounds, (w, h) => ({
      x: right ? anchor.x + 8 : anchor.x - 8 - w,
      y: anchor.y - h / 2,
    }));
  }

  // --- shared -------------------------------------------------------------

  #paintTooltip(
    rc: RenderContext,
    theme: Theme,
    font: FontSpec,
    content: TooltipContent,
    bounds: Rect,
    place: (width: number, height: number) => Point,
  ): void {
    const { heading, rows } = content;
    const lineH = rc.lineHeight(font);
    const bold = { ...font, weight: 600 };
    let w = rc.measureWidth(heading, bold);
    for (const row of rows) w = Math.max(w, SWATCH + 6 + rc.measureWidth(row.text, font));
    const width = Math.ceil(w + PAD * 2);
    const height = Math.ceil(lineH * (rows.length + 1) + PAD * 2 - 2);
    const at = place(width, height);
    const box: Rect = {
      x: Math.max(bounds.x, Math.min(at.x, bounds.x + bounds.width - width)),
      y: Math.max(bounds.y, Math.min(at.y, bounds.y + bounds.height - height)),
      width,
      height,
    };

    theme.drawTooltip(rc, box);
    const text = theme.tooltipTextColor();
    let y = box.y + PAD - 1;
    rc.drawText(heading, box.x + PAD, y, bold, text, { baseline: "top" });
    for (const row of rows) {
      y += lineH;
      if (row.color.a > 0) rc.fillRect({ x: box.x + PAD, y: y + (lineH - SWATCH) / 2, width: SWATCH, height: SWATCH }, row.color, 2);
      rc.drawText(row.text, box.x + PAD + SWATCH + 6, y, font, text, { baseline: "top" });
    }
  }
}

type Placed = { c: number; p: Point };

/**
 * The placed points of a series in unbroken runs, each with its index: a value with no place - a
 * gap, no x, or nothing a log axis can show - ends one run and starts the next.
 */
function placedRuns(count: number, placed: (c: number) => Point | null): Placed[][] {
  const runs: Placed[][] = [];
  let run: Placed[] = [];
  for (let c = 0; c < count; c++) {
    const p = placed(c);
    if (p) {
      run.push({ c, p });
      continue;
    }
    if (run.length) runs.push(run);
    run = [];
  }
  if (run.length) runs.push(run);
  return runs;
}

/**
 * A run cut down to the points in view and one neighbor either side of each stretch of them, so a
 * line drawn from it still leaves the plot at the right angle. A run with nothing in view is gone.
 */
function clipRun(run: readonly Placed[], inView: (u: number) => boolean): Placed[][] {
  const keep = run.map((r, k) => inView(r.p.x) || (k > 0 && inView(run[k - 1]!.p.x)) || (k < run.length - 1 && inView(run[k + 1]!.p.x)));
  if (keep.every(Boolean)) return [[...run]];
  const out: Placed[][] = [];
  let cur: Placed[] = [];
  run.forEach((r, k) => {
    if (keep[k]) {
      cur.push(r);
      return;
    }
    if (cur.length) out.push(cur);
    cur = [];
  });
  if (cur.length) out.push(cur);
  return out;
}

/**
 * A polyline cut where it crosses `level` (in its y), into pieces each wholly on one side, the
 * crossing point shared by the two pieces either side of it. `side` is the sign of y - level.
 */
function splitAtLevel(points: readonly Point[], level: number): { side: number; points: Point[] }[] {
  const out: { side: number; points: Point[] }[] = [];
  if (points.length === 0) return out;
  let side = Math.sign(points[0]!.y - level);
  let current: Point[] = [points[0]!];
  for (let k = 1; k < points.length; k++) {
    const a = points[k - 1]!;
    const b = points[k]!;
    const next = Math.sign(b.y - level);
    if (side === 0) side = next;
    if (next !== 0 && side !== 0 && next !== side) {
      const t = (level - a.y) / (b.y - a.y);
      const m = { x: a.x + (b.x - a.x) * t, y: level };
      current.push(m);
      out.push({ side, points: current });
      current = [m];
      side = next;
    }
    current.push(b);
  }
  out.push({ side, points: current });
  return out;
}
